/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState, useRef } from "react";
import { Node0Terminal } from "./components/Node0Terminal";
import { HolographicDatabase } from "./components/HolographicDatabase";
import { ExtractionFunnel } from "./components/ExtractionFunnel";
import { CosmicTabPanel } from "./components/CosmicTabPanel";
import { CosmicLandingTabPanel } from "./components/CosmicLandingTabPanel";
import { PayloadMatrix } from "./components/PayloadMatrix";
import { SiteEditor } from "./components/SiteEditor";
import { BrandingTerminal } from "./components/BrandingTerminal";
import { CrmDashboardTerminal } from "./components/CrmDashboardTerminal";
import { SecretaryNode } from "./components/SecretaryNode";
import { LegalStrikeModal } from "./components/LegalStrikeModal";
import { FiscalStrikeModal } from "./components/FiscalStrikeModal";
import { NexoraXHotkeyHelpModal } from "./components/NexoraXHotkeyHelpModal";
import { NexoraXConfirmationDialog } from "./components/NexoraXConfirmationDialog";
import { ArchitectVoiceModal } from "./components/ArchitectVoiceModal";
import { RogueEntityTerminal } from "./components/RogueEntityTerminal";
import { SpectacleCommandTerminal } from "./components/SpectacleCommandTerminal";
import { RealityAuditTerminal } from "./components/RealityAuditTerminal";
import { NexoraxChat } from "./components/NexoraxChat";
import { NexusControlPlane } from "./components/NexusControlPlane";
import { NexoraXPaymentGateway, NexoraXPaymentTabs } from "./components/NexoraXPaymentGateway";
import { NexoraxCoreNode } from "./components/CoreNodeDashboard";
import { NexoraXLedgerTicker } from "./components/NexoraXLedgerTicker";
import { NexoraXHeaderNavigation } from "./components/NexoraXHeaderNavigation";
import { NexoraxCloudHighlight } from "./components/NexoraxCloudHighlight";
import { NexoraX27CategoriesHub } from "./components/NexoraX27CategoriesHub";
import { NexoraX27Hub } from "./components/NexoraX27Hub";
import { NexoraX27WorkspacePage } from "./components/NexoraX27WorkspacePage";
import { NexoraXCategory27Console } from "./components/NexoraXCategory27Console";
import { NexoraxSearchEngine } from "./components/NexoraxSearchEngine";
import { NexoraxWorkspace } from "./components/NexoraxWorkspace";
import { NexoraXLegalFooter } from "./components/NexoraXLegalFooter";
import { NexoraXConciergeDock } from "./components/NexoraXConciergeDock";
import { NexoraXOperatorConsole } from "./components/NexoraXOperatorConsole";
import { NexoraXLegalDocuments } from "./components/NexoraXLegalDocuments";
import { AdminControlSafeFallback } from "./components/AdminControlSafeFallback";
import NexoraXRemoteWorkplace from "./components/NexoraXRemoteWorkplace";
import NexoraXSmartQuote from "./components/NexoraXSmartQuote";
import NexoraXClientPreviewPortal from "./components/NexoraXClientPreviewPortal";
import NexoraXShowcaseGallery from "./components/NexoraXShowcaseGallery";
import NexoraXQRCampaignGenerator from "./components/NexoraXQRCampaignGenerator";
import NexoraXCampaignLaunchPad from "./components/NexoraXCampaignLaunchPad";
import NexoraXCampaignHub from "./components/NexoraXCampaignHub";
import NexoraXRemoteTrainingHub from "./components/NexoraXRemoteTrainingHub";
import { ShieldAlert, Search, Menu, X, ChevronDown, Cpu, Shield, Terminal, Loader2, Database, Zap, RefreshCw, Keyboard, LogOut, AlertTriangle, Lock } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { CATEGORIES, COMPANY_DIVISIONS } from "./data";
import { useSiteContent } from "./contexts/SiteContentContext";
import { useAuth } from "./lib/AuthContext";
import { useToast } from "./contexts/ToastContext";


export default function App() {
  const isLiveWebsite = () => {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname.toLowerCase();
    
    // Developer or Preview sandbox environments
    const isDevEnv = 
      host.includes("localhost") || 
      host.includes("127.0.0.1") || 
      host.includes("run.app") || 
      host.includes("web.app");
      
    // It's the live domain context if it's NOT a dev environment, or matches their official domain
    return !isDevEnv || host.includes("nexorax") || host.includes("nexoraxdigital.co.uk");
  };

  const [isVaultUnlocked, setIsVaultUnlocked] = useState<boolean>(false);
  const [vaultPhase, setVaultPhase] = useState<
    "vault" | "extraction" | "plan" | "locked" | null
  >(null);
  const [phaseTimeLeft, setPhaseTimeLeft] = useState<number | null>(null);
  const [adminVerified, setAdminVerified] = useState(false);
  const [adminRole, setAdminRole] = useState<"CLIENT" | "ADMIN" | "SUPER_ADMIN">("CLIENT");
  const [isSecViolation, setIsSecViolation] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [activeMainTab, setActiveMainTab] = useState<string>(
    "NODE 0: ARCHITECT TERMINAL",
  );
  const [activeSubTab, setActiveSubTab] = useState<string | null>(null);
  const [activeLandingTab, setActiveLandingTab] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const legalPaths = ["/privacy-policy", "/cookie-policy", "/terms", "/refund-policy", "/cancellation-policy", "/cookie-settings", "/accessibility", "/privacy-rights-request", "/contact"];
      if (legalPaths.includes(path)) {
        return path;
      }
      if (path === "/pricing") return "Tiers";
      if (path === "/login") return "Login";
      if (path === "/signup" || path === "/sign-up") return "Join";
      if (path === "/services") return "Services / Solutions";
      if (path === "/marketplace") return "NexoraX Marketplace";
      if (path === "/digital-products") return "Digital Products";
      if (path === "/product-builder") return "Product Builder";
      if (path === "/my-projects") return "My Projects";
      if (path === "/my-purchases") return "My Purchases";
      if (path === "/nexus-os") return "Nexus OS";
      if (path === "/guides-videos") return "Guides & Videos";
      if (path === "/nexus-search") return "Nexus Search";
      if (path === "/nexus-hosting") return "Nexus Hosting";
      if (path === "/nexus-mail-domains") return "Nexus Mail & Domains";
      if (path === "/nexus-galaxy-lab") return "Nexus Galaxy Lab";
      if (path === "/operator-console") return "Operator Console";
      if (path === "/remote-workplace") return "Remote Workplace";
      if (path === "/smart-quote") return "Smart Quote";
      if (path === "/operator-onboarding") return "Operator Onboarding";
      if (path === "/operator-login") return "Operator Login";
      if (path === "/admin-control") return "Login";
      
      return "Home";
    }
    return "Home";
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileMatrixOpen, setIsMobileMatrixOpen] = useState(false);
  const [showLegalStrike, setShowLegalStrike] = useState(false);
  const [showFiscalStrike, setShowFiscalStrike] = useState(false);
  const [showArchitectVoice, setShowArchitectVoice] = useState(false);
  const [voiceAssistantMode, setVoiceAssistantMode] = useState<'standard' | 'concierge'>('standard');
  const [showHotkeyHelp, setShowHotkeyHelp] = useState(false);
  const [hasCriticalAlert, setHasCriticalAlert] = useState(false);

  // User inactivity session tracking (15-minute threshold)
  const [inactivitySeconds, setInactivitySeconds] = useState<number>(0);
  const [showInactivityWarning, setShowInactivityWarning] = useState<boolean>(false);

  useEffect(() => {
    const handleResetInactivity = () => {
      setInactivitySeconds(0);
      setShowInactivityWarning(false);
    };

    const interactionEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    interactionEvents.forEach(evt => {
      window.addEventListener(evt, handleResetInactivity);
    });

    const ticker = setInterval(() => {
      const isPersistentDir = (user?.email?.toLowerCase().trim() === "siddique.mahmood@gmail.com") || 
                              (typeof window !== 'undefined' && !!localStorage.getItem("nexorax_director_session"));
      if (isPersistentDir) {
        setInactivitySeconds(0);
        setShowInactivityWarning(false);
        return;
      }
      setInactivitySeconds(prev => {
        const nextVal = prev + 1;
        // 14 minutes = 840 seconds
        if (nextVal >= 840 && nextVal < 900) {
          setShowInactivityWarning(true);
        } else if (nextVal >= 900) {
          // Exceeds 15 minutes - session expired
          setShowInactivityWarning(false);
          setIsVaultUnlocked(false);
          setVaultPhase(null);
          setPhaseTimeLeft(null);
        }
        return nextVal;
      });
    }, 1000);

    return () => {
      interactionEvents.forEach(evt => {
        window.removeEventListener(evt, handleResetInactivity);
      });
      clearInterval(ticker);
    };
  }, []);

  // Global NexoraX Loader Integration states (Dark Cyber-Aesthetic)
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStep, setLoadingStep] = useState(0);

  const [showGlobalCookieBanner, setShowGlobalCookieBanner] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return !localStorage.getItem("nexorax_cookie_consent_v2");
    }
    return false;
  });

  const loadingSteps = [
    "COGNITIVE SHIELD ACTIVATED - READYING NEURAL INTERFACES...",
    "HANDSHAKE PROTOCOL SHIELD [NODE-09.2] BOOTSTRAPPING...",
    "VERIFYING NEXORAX SECURITY CHECKS BLOCKLIST (PERSISTENT SQL ACTIVITY LOG READY)...",
    "ESTABLISHING CONNECTIVITY INTEGRITY TO Secure Payment Provider SECURE SWIFT CORE...",
    "MOUNTING COGNITIVE DEFENSE MATRIX ON AMD EPYC 128 CORES HARDWARE...",
    "INJECTING SUB-PENNY TRANSACTION RULES FOR CHARITABLE LEVY GRIDS...",
    "DECRYPTING NexoraX Resource Library AT GOOGLE DRIVE CloudSync-02 PORT...",
    "COGNITIVE DEFENSE INTEGRATED. OS READY FOR ADMIN MANDATES."
  ];

  useEffect(() => {
    let interval: any;
    interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        const r = Math.random();
        // Uneven progress jumps for realism
        const increment = r > 0.8 ? Math.floor(Math.random() * 15) + 8 : Math.floor(Math.random() * 5) + 3;
        return Math.min(prev + increment, 100);
      });
    }, 120);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const stepIndex = Math.min(
      Math.floor((loadingProgress / 100) * loadingSteps.length),
      loadingSteps.length - 1
    );
    setLoadingStep(stepIndex);

    if (loadingProgress >= 100) {
      const timer = setTimeout(() => {
        setIsAppLoading(false);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [loadingProgress]);

  const [gridOpacity, setGridOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_grid_opacity');
      return saved !== null ? parseFloat(saved) : 0.3;
    }
    return 0.3;
  });

  const [videoBrightness, setVideoBrightness] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_brightness');
      if (saved === null || saved === '1.2' || saved === '0.35' || saved === '0.15') {
        localStorage.setItem('nexorax_video_brightness', '0.08');
        return 0.08;
      }
      return parseFloat(saved);
    }
    return 0.08;
  });

  const [videoAutoDim, setVideoAutoDim] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_autodim');
      return saved === 'true';
    }
    return false;
  });

  const [videoInteracting, setVideoInteracting] = useState(false);

  const [videoOpacity, setVideoOpacity] = useState(() => {
    if (typeof window !== 'undefined') {
       const saved = localStorage.getItem('nexorax_video_opacity');
       if (saved === null || saved === '0.25') {
         localStorage.setItem('nexorax_video_opacity', '0.75');
         return 0.75;
       }
       return parseFloat(saved);
    }
    return 0.75;
  });

  const [videoBlur, setVideoBlur] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_blur');
      return saved === 'true';
    }
    return false;
  });

  const [currentVideoSrc, setCurrentVideoSrc] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('nexorax_current_video_src') || '/brain1.mp4';
    }
    return '/brain1.mp4';
  });

  const [videoAutoplay, setVideoAutoplay] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_autoplay');
      return saved !== 'false'; // Defaults to true
    }
    return true;
  });

  const [videoPaused, setVideoPaused] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_paused');
      return saved === 'true'; // Defaults to false
    }
    return false;
  });

  const [videoTransitionDuration, setVideoTransitionDuration] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax_video_transition_duration');
      return saved !== null ? parseInt(saved, 10) : 1000;
    }
    return 1000;
  });

  const [videoTransitionOpacity, setVideoTransitionOpacity] = useState(1);
  const transitionDurationRef = useRef(videoTransitionDuration);
  const transitionToVideoRef = useRef<(newSrc: string) => void>(() => {});

  const [bgVisualMode, setBgVisualMode] = useState<'Cinematic Dark' | 'Balanced' | 'Extra Dark' | 'Original'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('nexorax_bg_visual_mode') as any) || 'Cinematic Dark';
    }
    return 'Cinematic Dark';
  });

  useEffect(() => {
    const handleVisualModeChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setBgVisualMode(customEvent.detail);
      }
    };
    window.addEventListener('nexorax-bg-visual-mode-change', handleVisualModeChange);
    return () => {
      window.removeEventListener('nexorax-bg-visual-mode-change', handleVisualModeChange);
    };
  }, []);

  const getAppVideoFilter = () => {
    if (bgVisualMode === 'Original') {
      return `brightness(${(videoAutoDim && videoInteracting) ? videoBrightness * 0.5 : videoBrightness}) blur(${videoBlur ? '14px' : '0px'})`;
    }
    const isMobileDevice = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobileDevice) {
      return 'brightness(0.32) contrast(1.05) saturate(0.8) blur(0px)';
    }

    let b = 0.38;
    let c = 1.08;
    let s = 0.85;
    let bl = videoBlur ? '14px' : '0.4px';

    if (bgVisualMode === 'Balanced') {
      b = 0.48;
      c = 1.06;
      s = 0.9;
      bl = '0px';
    } else if (bgVisualMode === 'Extra Dark') {
      b = 0.28;
      c = 1.04;
      s = 0.75;
      bl = '0.2px';
    }

    if (videoAutoDim && videoInteracting) {
      b = b * 0.5;
    }

    return `brightness(${b}) contrast(${c}) saturate(${s}) blur(${bl})`;
  };

  const [lastTriggeredHotkey, setLastTriggeredHotkey] = useState<{
    name: string;
    action: string;
    keyString: string;
    timestamp: number;
  } | null>(null);

  useEffect(() => {
    transitionDurationRef.current = videoTransitionDuration;
  }, [videoTransitionDuration]);

  useEffect(() => {
    transitionToVideoRef.current = (newSrc: string) => {
      const currentSrcClean = new URL(currentVideoSrc, window.location.href).href;
      const targetSrcClean = new URL(newSrc, window.location.href).href;
      if (currentSrcClean === targetSrcClean) {
        return;
      }
      setVideoTransitionOpacity(0);
      setTimeout(() => {
        setCurrentVideoSrc(newSrc);
        localStorage.setItem('nexorax_current_video_src', newSrc);
        setTimeout(() => {
          setVideoTransitionOpacity(1);
        }, 50);
      }, transitionDurationRef.current);
    };
  }, [currentVideoSrc]);

  const backgroundVideoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = backgroundVideoRef.current;
    if (video) {
      if (videoPaused) {
        video.pause();
      } else {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch((e) => console.warn("Background video play command thwarted:", e));
        }
      }
    }
  }, [videoPaused, currentVideoSrc]);

  useEffect(() => {
    const handleGridOpacityUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setGridOpacity(customEvent.detail);
      }
    };
    const handleBrightnessUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoBrightness(customEvent.detail);
        localStorage.setItem('nexorax_video_brightness', String(customEvent.detail));
      }
    };
    const handleOpacityUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoOpacity(customEvent.detail);
        localStorage.setItem('nexorax_video_opacity', String(customEvent.detail));
      }
    };
    const handleBlurUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoBlur(customEvent.detail);
        localStorage.setItem('nexorax_video_blur', String(customEvent.detail));
      }
    };
    const handleVideoCycle = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        transitionToVideoRef.current(customEvent.detail);
      }
    };
    const handleAutoplayUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoAutoplay(customEvent.detail);
        localStorage.setItem('nexorax_video_autoplay', String(customEvent.detail));
      }
    };
    const handlePlayPauseUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoPaused(customEvent.detail);
        localStorage.setItem('nexorax_video_paused', String(customEvent.detail));
      }
    };
    const handleTransitionDurationUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoTransitionDuration(customEvent.detail);
        localStorage.setItem('nexorax_video_transition_duration', String(customEvent.detail));
      }
    };
    const handleAutoDimToggleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoAutoDim(customEvent.detail);
        localStorage.setItem('nexorax_video_autodim', String(customEvent.detail));
      }
    };
    const handleInteractingUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail !== undefined) {
        setVideoInteracting(customEvent.detail);
      }
    };

    window.addEventListener('nexorax-grid-opacity', handleGridOpacityUpdate);
    window.addEventListener('nexorax-video-brightness', handleBrightnessUpdate);
    window.addEventListener('nexorax-video-opacity', handleOpacityUpdate);
    window.addEventListener('nexorax-video-blur', handleBlurUpdate);
    window.addEventListener('nexorax-video-cycle', handleVideoCycle);
    window.addEventListener('nexorax-video-autoplay', handleAutoplayUpdate);
    window.addEventListener('nexorax-video-playpause', handlePlayPauseUpdate);
    window.addEventListener('nexorax-video-transition-duration', handleTransitionDurationUpdate);
    window.addEventListener('nexorax-video-autodim-toggle', handleAutoDimToggleUpdate);
    window.addEventListener('nexorax-sec-node-interacting', handleInteractingUpdate);

    return () => {
      window.removeEventListener('nexorax-grid-opacity', handleGridOpacityUpdate);
      window.removeEventListener('nexorax-video-brightness', handleBrightnessUpdate);
      window.removeEventListener('nexorax-video-opacity', handleOpacityUpdate);
      window.removeEventListener('nexorax-video-blur', handleBlurUpdate);
      window.removeEventListener('nexorax-video-cycle', handleVideoCycle);
      window.removeEventListener('nexorax-video-autoplay', handleAutoplayUpdate);
      window.removeEventListener('nexorax-video-playpause', handlePlayPauseUpdate);
      window.removeEventListener('nexorax-video-transition-duration', handleTransitionDurationUpdate);
      window.removeEventListener('nexorax-video-autodim-toggle', handleAutoDimToggleUpdate);
      window.removeEventListener('nexorax-sec-node-interacting', handleInteractingUpdate);
    };
  }, []);
  const { content } = useSiteContent();
  const { user, signInWithGoogle, logout, companyDivisions, authServiceUnavailable } = useAuth();
  const { showToast } = useToast();

  if (typeof window !== "undefined" && (window.location.pathname === "/remote-workplace" || window.location.pathname.startsWith("/remote-workplace"))) {
    return <NexoraXRemoteWorkplace />;
  }

  // SMART_QUOTE_ROUTE_GUARD_PATCH
  if (typeof window !== "undefined" && (window.location.pathname === "/smart-quote" || window.location.pathname.startsWith("/smart-quote"))) {
    return <NexoraXSmartQuote />;
  }

  // CLIENT_PREVIEW_ROUTE_PATCH
  if (typeof window !== "undefined" && (window.location.pathname === "/client-preview" || window.location.pathname.startsWith("/client-preview"))) {
    return <NexoraXClientPreviewPortal />;
  }

  // SHOWCASE_GALLERY_ROUTE_PATCH
  if (typeof window !== "undefined" && (window.location.pathname === "/showcase" || window.location.pathname.startsWith("/showcase") || window.location.pathname === "/demo-gallery" || window.location.pathname.startsWith("/demo-gallery"))) {
    return <NexoraXShowcaseGallery />;
  }

  // QR_CAMPAIGN_ROUTE_PATCH
  if (typeof window !== "undefined" && (window.location.pathname === "/qr-campaign" || window.location.pathname.startsWith("/qr-campaign") || window.location.pathname === "/qr-campaign-generator" || window.location.pathname.startsWith("/qr-campaign-generator"))) {
    return <NexoraXQRCampaignGenerator />;
  }

  // CAMPAIGN_HUB_ROUTE_PATCH
if (typeof window !== "undefined" && (window.location.pathname === "/campaign-hub" || window.location.pathname.startsWith("/campaign-hub"))) {
  return <NexoraXCampaignHub />;
}

// CAMPAIGN_LAUNCH_PAD_ROUTE_PATCH
  if (typeof window !== "undefined" && (window.location.pathname === "/campaign-launch-pad" || window.location.pathname.startsWith("/campaign-launch-pad") || window.location.pathname === "/launch-pad" || window.location.pathname.startsWith("/launch-pad"))) {
    return <NexoraXCampaignLaunchPad />;
  }

  // REMOTE_TRAINING_ROUTE_PATCH
  if (typeof window !== "undefined" && (window.location.pathname === "/remote-training" || window.location.pathname.startsWith("/remote-training"))) {
    return <NexoraXRemoteTrainingHub />;
  }

  if (authServiceUnavailable) {
    return (
      <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-[#010101] text-slate-300 font-mono p-6">
        <div className="w-full max-w-md p-8 rounded-2xl border border-red-500/30 bg-red-950/10 text-center space-y-6 shadow-[0_0_30px_rgba(239,68,68,0.1)]">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full border border-red-500/30 flex items-center justify-center bg-red-950/20">
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-sm font-bold uppercase tracking-wider text-red-400">Security / Auth Timeout</h1>
            <p className="text-xs text-slate-400 font-sans leading-relaxed">
              Authentication service unavailable. Please retry or sign in again.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2.5 rounded-xl bg-red-950/30 hover:bg-red-950/50 border border-red-500/20 hover:border-red-500/40 text-red-300 text-xs font-black uppercase tracking-widest cursor-pointer transition-all"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const REMOVED_CATEGORIES: string[] = [];

  // Dynamically extract the full list of 111 sub-category nodes as requested by the Director
  const all111SubNodes = React.useMemo(() => {
    const list: Array<{ name: string; categoryTitle: string; categoryId: number }> = [];
    CATEGORIES.forEach(cat => {
      cat.nodes.forEach(node => {
        list.push({
          name: node,
          categoryTitle: cat.title,
          categoryId: cat.id
        });
      });
    });
    
    // Safety buffer to ensure precisely 111 nodes
    while (list.length < 111) {
      list.push({
        name: `Neural Synapse Protocol #${list.length + 1}`,
        categoryTitle: "RESTORATION CORE",
        categoryId: 1
      });
    }
    
    return list.slice(0, 111);
  }, []);

  const handleHeaderLogout = async () => {
    const triggerSeverance = async () => {
      try {
        await logout();
        sessionStorage.removeItem("nexorax_mfa_verified");
        localStorage.removeItem("nexorax_director_session");
        setMfaVerified(false);
        showToast("Node session successfully severed. Safely disconnected active channels.", "info");
        setActiveLandingTab("Login");
      } catch (err) {
        console.error("Logout execution failed:", err);
        showToast("Authentication sever error occurred during disconnect.", "error");
      }
    };

    if (window.confirmNexoraXAction) {
      window.confirmNexoraXAction({
        title: "CONFIRM SESSION SEVERANCE",
        message: "You are about to terminate the active secure connection to the NexoraX Neural Core. This will revoke credentials, secure routing logs, and return to the gateway.",
        confirmLabel: "SEVER SESSION",
        cancelLabel: "ABORT DISCONNECT",
        impactLevel: "HIGH",
        nodeName: "CORE OVERWATCH"
      }, triggerSeverance);
    } else {
      if (window.confirm("NEXORAX DISCONNECT:\n\nAre you sure you want to sever your active secure session?")) {
        triggerSeverance();
      }
    }
  };

  const PUBLIC_SELLABLE_CATEGORIES = [
  {
    id: 201,
    title: "AI Automation",
    nodes: ["AI Sales Agent", "Workflow Automation", "Document Processing AI", "Voice Assistant", "Autonomous Agent Swarm"],
  },
  {
    id: 202,
    title: "Websites & Apps",
    nodes: ["Business Website", "Web App", "Mobile App", "Product Builder", "Customer Portal"],
  },
  {
    id: 203,
    title: "Chat Agents",
    nodes: ["Website Chat Agent", "Customer Support Bot", "Booking Assistant", "FAQ Assistant"],
  },
  {
    id: 204,
    title: "Sales Agents",
    nodes: ["Lead Finder", "Deal Closer", "Vanguard Closer", "Hermes Outreach", "Client Deal Registry"],
  },
  {
    id: 205,
    title: "SEO & Growth",
    nodes: ["SEO Pages", "Google Visibility", "Trend Scout", "Offer Ladder", "Organic Growth Engine"],
  },
  {
    id: 206,
    title: "Media & Branding",
    nodes: ["Logo Pack", "Video Generator", "Social Media Kit", "Brand Identity", "Content Studio"],
  },
  {
    id: 207,
    title: "QR Engine",
    nodes: ["QR Campaigns", "Sticker QR", "Menu QR", "Tracking QR", "Lead Capture QR"],
  },
  {
    id: 208,
    title: "Code Engine",
    nodes: ["Code Generator", "Program Engine", "API Builder", "Automation Scripts", "Quality Assurance AI"],
  },
  {
    id: 209,
    title: "Email, Domain & Hosting",
    nodes: ["Business Email", "Domain Setup", "Hosting Package", "SSL Setup", "Server Monitoring"],
  },
  {
    id: 210,
    title: "Deliver & Driver Operations",
    nodes: ["Courier Dispatch", "Driver Route Planner", "Delivery Tracking", "Proof Of Delivery", "Driver Admin Dashboard"],
  },
  {
    id: 211,
    title: "CRM / Dashboards",
    nodes: ["CRM Dashboard", "Lead Pipeline", "Client Portal", "Invoice Register", "Performance Reports"],
  },
  {
    id: 212,
    title: "Legal Docs / Contracts",
    nodes: ["Terms Pages", "Privacy Policy", "Service Agreement", "Invoice Agreement", "Compliance Register"],
  },
  {
    id: 213,
    title: "Grants & Funding",
    nodes: ["Grant Finder", "Funding Pack", "Application Draft", "Evidence Checklist", "Cashflow Forecast"],
  },
  {
    id: 214,
    title: "Sports Management",
    nodes: ["Team Website", "Fixture Dashboard", "Sponsorship Pack", "Membership Portal", "Club Media Kit"],
  },
];

const baseCategories = (content.categories || CATEGORIES).filter(
    cat => !REMOVED_CATEGORIES.includes(cat.title)
  );

  const initialCategories = [
    ...baseCategories,
    ...PUBLIC_SELLABLE_CATEGORIES.filter(
      publicCategory => !baseCategories.some(cat => cat.title === publicCategory.title)
    ),
  ];

  const [categoryOrder, setCategoryOrder] = useState(initialCategories);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(1);

  useEffect(() => {
    setCategoryOrder(initialCategories);
  }, [content.categories]);

  const [mfaVerified, setMfaVerified] = useState(false);

  useEffect(() => {
    const handleMfaVerified = () => {};
    const handleTriggerLandingTab = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        if (customEvent.detail === "Admin Panel") {
          setActiveMainTab("NODE 0: ARCHITECT TERMINAL");
        }
        setActiveLandingTab(customEvent.detail);
      }
    };
    const handleTriggerMainTab = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setActiveLandingTab(null);
        setActiveMainTab(customEvent.detail);
        setActiveSubTab(null);
        setActiveDropdown(null);
      }
    };
    const handleVoiceNavigate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        const { mainTab, landingTab, subTab } = customEvent.detail;
        if (mainTab !== undefined) {
          setActiveMainTab(mainTab);
        }
        if (landingTab !== undefined) {
          setActiveLandingTab(landingTab);
        }
        if (subTab !== undefined) {
          setActiveSubTab(subTab);
        }
      }
    };
    const handleVoiceAction = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        const { action } = customEvent.detail;
        if (action === "LOCK_TERMINAL") {
          handleHeaderLogout();
        } else if (action === "SHOW_HELP") {
          setShowHotkeyHelp(true);
        }
      }
    };
    window.addEventListener("mfa-verified", handleMfaVerified);
    window.addEventListener("nexorax-trigger-landing-tab", handleTriggerLandingTab);
    window.addEventListener("nexorax-trigger-main-tab", handleTriggerMainTab);
    window.addEventListener("nexorax-voice-navigate", handleVoiceNavigate);
    window.addEventListener("nexorax-voice-action", handleVoiceAction);
    return () => {
      window.removeEventListener("mfa-verified", handleMfaVerified);
      window.removeEventListener("nexorax-trigger-landing-tab", handleTriggerLandingTab);
      window.removeEventListener("nexorax-trigger-main-tab", handleTriggerMainTab);
      window.removeEventListener("nexorax-voice-navigate", handleVoiceNavigate);
      window.removeEventListener("nexorax-voice-action", handleVoiceAction);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const verifyAdmin = async () => {
      setAdminVerified(false);
      setAdminRole("CLIENT");
      setMfaVerified(false);

      if (!user) {
        setIsVaultUnlocked(false);
        setVaultPhase(null);
        setPhaseTimeLeft(null);
        return;
      }

      try {
        const token = await user.getIdToken();
        const response = await fetch("/api/admin/verify-clearance", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        const data = await response.json();
        const verifiedRole = data?.role === "SUPER_ADMIN" || data?.role === "ADMIN" || data?.role === "DIRECTOR" || data?.role === "OPERATOR";

        if (!cancelled && response.ok && verifiedRole) {
          setAdminVerified(true);
          setAdminRole(data.role);
          setMfaVerified(true);
          setIsVaultUnlocked(true);
          setVaultPhase("vault");
          setPhaseTimeLeft(15 * 60);

          if (data.role === "OPERATOR") {
            setActiveLandingTab("Operator Console");
          } else {
            setActiveLandingTab("Admin Panel");
            setActiveMainTab("Admin Panel");
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Admin verification failed:", err);
        }
      }
    };

    verifyAdmin();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Never grant admin UI privileges from an email string or browser storage.
  // Only the Firebase token checked by /api/admin/verify-clearance grants this status.
  const isDirector = !!user && adminVerified &&
                     (adminRole === "ADMIN" || adminRole === "SUPER_ADMIN" || adminRole === "DIRECTOR");
  const isStaff = isDirector || adminRole === "OPERATOR" || adminRole === "ADMIN" || adminRole === "SUPER_ADMIN" || adminRole === "DIRECTOR";

  useEffect(() => {
    (window as any).__nexoraxAdminVerified = isDirector;
    if (typeof (window as any).runSecurityCloakingCheck === 'function') {
      (window as any).runSecurityCloakingCheck();
    }
  }, [isDirector]);

  // Debug-safe admin status check
  useEffect(() => {
    console.log("[NEXORAX DEBUG STATUS]", {
      authenticatedEmail: user?.email || "NOT_AUTHENTICATED",
      isAdmin: isDirector,
      gateBypass: isDirector
    });
  }, [user, isDirector]);

  const [currentTime, setCurrentTime] = useState(new Date());
  const dropdownTimeout = useRef<number | null>(null);

  // Global Search System
  const [globalSearch, setGlobalSearch] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Focus click outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cmd/Ctrl + K Focus shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      if (cmdOrCtrl && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // NexoraX NexoraX Global Hotkeys runtime key scanner
  useEffect(() => {
    let activeHotkeys: any[] = [];
    
    const loadHotkeys = () => {
      try {
        const cached = localStorage.getItem('nexorax_NexoraX_hotkeys');
        if (cached) {
          activeHotkeys = JSON.parse(cached);
          // Auto inject new toggle-marketplace hotkey if not present in cached older hotkeys version
          let hasUpdated = false;
          if (!activeHotkeys.find(h => h.id === 'toggle-marketplace')) {
            activeHotkeys.push({ id: 'toggle-marketplace', action: 'TOGGLE_MARKETPLACE_VIEW', key: 'm', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false });
            hasUpdated = true;
          }
          if (!activeHotkeys.find(h => h.id === 'toggle-help')) {
            activeHotkeys.push({ id: 'toggle-help', action: 'TOGGLE_HOTKEY_HELP', key: 'k', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false });
            hasUpdated = true;
          }
          if (!activeHotkeys.find(h => h.id === 'goto-spectacle')) {
            activeHotkeys.push({ id: 'goto-spectacle', action: 'GOTO_SPECTACLE_COMMAND', key: 's', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false });
            hasUpdated = true;
          }
          if (hasUpdated) {
            localStorage.setItem('nexorax_NexoraX_hotkeys', JSON.stringify(activeHotkeys));
          }
          return;
        }
      } catch (err) {
        console.error("Failed to parse cached hotkeys:", err);
      }
      activeHotkeys = [
        { id: 'toggle-marketplace', action: 'TOGGLE_MARKETPLACE_VIEW', key: 'm', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'toggle-admin', action: 'TOGGLE_MARKETPLACE_ADMIN', key: 'a', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'toggle-detail', action: 'TOGGLE_DETAIL_MODE', key: 'd', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'add-cart', action: 'ADD_TO_LEDGER', key: 'h', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'clear-cart', action: 'CLEAR_LEDGER_CART', key: 'c', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'cycle-geometry', action: 'CYCLE_GEOMETRY', key: 'g', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'random-asset', action: 'SELECT_RANDOM_ASSET', key: 'r', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'filter-all', action: 'FILTER_ALL', key: '0', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false },
        { id: 'filter-infra', action: 'FILTER_INFRASTRUCTURE', key: '1', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false },
        { id: 'filter-comms', action: 'FILTER_COMMUNICATIONS', key: '2', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false },
        { id: 'filter-neural', action: 'FILTER_NEURAL', key: '3', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false },
        { id: 'filter-workspace', action: 'FILTER_WORKSPACE', key: '4', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false },
        { id: 'filter-security', action: 'FILTER_SECURITY', key: '5', ctrlKey: true, altKey: false, shiftKey: false, metaKey: false },
        { id: 'next-page', action: 'NEXT_PAGE', key: 'ArrowRight', ctrlKey: false, altKey: false, shiftKey: false, metaKey: false },
        { id: 'prev-page', action: 'PREV_PAGE', key: 'ArrowLeft', ctrlKey: false, altKey: false, shiftKey: false, metaKey: false },
        { id: 'goto-spectacle', action: 'GOTO_SPECTACLE_COMMAND', key: 's', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false },
        { id: 'toggle-help', action: 'TOGGLE_HOTKEY_HELP', key: 'k', ctrlKey: false, altKey: true, shiftKey: false, metaKey: false }
      ];
    };

    loadHotkeys();

    const handleReload = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        activeHotkeys = customEvent.detail;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      if (activeEl) {
        const tag = activeEl.tagName.toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || activeEl.getAttribute('contenteditable') === 'true') {
          return;
        }
      }

      for (const hk of activeHotkeys) {
        const matchesKey = e.key.toLowerCase() === hk.key.toLowerCase();
        const matchesCtrl = e.ctrlKey === hk.ctrlKey;
        const matchesAlt = e.altKey === hk.altKey;
        const matchesShift = e.shiftKey === hk.shiftKey;
        const matchesMeta = e.metaKey === hk.metaKey;

        if (matchesKey && matchesCtrl && matchesAlt && matchesShift && matchesMeta) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('nexorax-hotkey-action', { detail: { action: hk.action } }));
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('nexorax-hotkeys-reloaded', handleReload);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('nexorax-hotkeys-reloaded', handleReload);
    };
  }, []);

  // Listen to TOGGLE_MARKETPLACE_VIEW and TOGGLE_HOTKEY_HELP global hotkey actions to trigger transitions/modals
  useEffect(() => {
    const handleGlobalHotkeyTap = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        // Trigger visual toast overlay for every single mapped hotkey action
        const actionDetails: Record<string, { name: string; key: string }> = {
          'TOGGLE_MARKETPLACE_VIEW': { name: 'Toggle NexoraX Marketplace View', key: 'Alt + M' },
          'TOGGLE_MARKETPLACE_ADMIN': { name: 'Toggle Marketplace Admin Portal', key: 'Alt + A' },
          'TOGGLE_DETAIL_MODE': { name: 'Toggle Product Detailed View', key: 'Alt + D' },
          'ADD_TO_LEDGER': { name: 'Trigger NexoraX Handshake', key: 'Alt + H' },
          'CLEAR_LEDGER_CART': { name: 'Reset Shopping Ledger', key: 'Alt + C' },
          'CYCLE_GEOMETRY': { name: 'Cycle 3D Wireframe Geometry', key: 'Alt + G' },
          'SELECT_RANDOM_ASSET': { name: 'Select Random Asset', key: 'Alt + R' },
          'FILTER_ALL': { name: 'Filter: ALL segments', key: 'Ctrl + 0' },
          'FILTER_INFRASTRUCTURE': { name: 'Filter: Infrastructure', key: 'Ctrl + 1' },
          'FILTER_COMMUNICATIONS': { name: 'Filter: Communications', key: 'Ctrl + 2' },
          'FILTER_NEURAL': { name: 'Filter: Neural Core', key: 'Ctrl + 3' },
          'FILTER_WORKSPACE': { name: 'Filter: Spatial Workspace', key: 'Ctrl + 4' },
          'FILTER_SECURITY': { name: 'Filter: Cryptographic Security', key: 'Ctrl + 5' },
          'NEXT_PAGE': { name: 'Navigate: Next Page Segment', key: 'ArrowRight' },
          'PREV_PAGE': { name: 'Navigate: Previous Page Segment', key: 'ArrowLeft' },
          'GOTO_SPECTACLE_COMMAND': { name: 'Navigate: Spectacle Command Node', key: 'Alt + S' },
          'TOGGLE_HOTKEY_HELP': { name: 'Toggle Keyboard Matrix Guide', key: 'Alt + K' }
        };

        let matchedName = '';
        let matchedKey = '';
        
        try {
          const cached = localStorage.getItem('nexorax_NexoraX_hotkeys');
          if (cached) {
            const parsed = JSON.parse(cached);
            const found = parsed.find((h: any) => h.action === customEvent.detail.action);
            if (found) {
              matchedName = found.name;
              const parts = [];
              if (found.ctrlKey) parts.push('Ctrl');
              if (found.altKey) parts.push('Alt');
              if (found.shiftKey) parts.push('Shift');
              if (found.metaKey) parts.push('Meta');
              let keyL = found.key;
              if (keyL === ' ') keyL = 'Space';
              else if (keyL.length === 1) keyL = keyL.toUpperCase();
              parts.push(keyL);
              matchedKey = parts.join(' + ');
            }
          }
        } catch (err) {
          console.error("Local load fail in hotkey toast lookup:", err);
        }

        if (!matchedName && actionDetails[customEvent.detail.action]) {
          matchedName = actionDetails[customEvent.detail.action].name;
          matchedKey = actionDetails[customEvent.detail.action].key;
        }

        setLastTriggeredHotkey({
          name: matchedName || customEvent.detail.action,
          action: customEvent.detail.action,
          keyString: matchedKey || 'Key Triggered',
          timestamp: Date.now()
        });

        if (customEvent.detail.action === 'TOGGLE_MARKETPLACE_VIEW') {
          setActiveLandingTab(prev => {
            if (prev === 'NexoraX Marketplace') {
              const before = sessionStorage.getItem('last_active_landing_tab') || 'Home';
              return before === 'NexoraX Marketplace' ? 'Home' : before;
            } else {
              if (prev) {
                sessionStorage.setItem('last_active_landing_tab', prev);
              }
              return 'NexoraX Marketplace';
            }
          });
        } else if (customEvent.detail.action === 'TOGGLE_HOTKEY_HELP') {
          setShowHotkeyHelp(prev => !prev);
        } else if (customEvent.detail.action === 'GOTO_SPECTACLE_COMMAND') {
          setActiveLandingTab(null);
          setActiveMainTab("SPECTACLE COMMAND");
          setActiveSubTab(null);
          setActiveDropdown(null);
        }
      }
    };

    window.addEventListener('nexorax-hotkey-action', handleGlobalHotkeyTap);
    return () => window.removeEventListener('nexorax-hotkey-action', handleGlobalHotkeyTap);
  }, []);

  // Hotkey triggered toast automatic expiration
  useEffect(() => {
    if (!lastTriggeredHotkey) return;
    const timer = setTimeout(() => {
      setLastTriggeredHotkey(null);
    }, 3500);
    return () => clearTimeout(timer);
  }, [lastTriggeredHotkey]);

  // Track page state changes for toggle reversion
  useEffect(() => {
    if (activeLandingTab && activeLandingTab !== 'NexoraX Marketplace') {
      sessionStorage.setItem('last_active_landing_tab', activeLandingTab);
    }
    // Dispatch a global event when landing tab changes so other views can sync
    window.dispatchEvent(new CustomEvent('nexorax-landing-tab-changed', { detail: activeLandingTab }));
  }, [activeLandingTab]);

  useEffect(() => {
    const handleTriggerTabChange = (e: Event) => {
      const targetTab = (e as CustomEvent).detail;
      if (targetTab) {
        setActiveLandingTab(targetTab);
        // Ensure standard landing dimension is active
        setActiveMainTab("NODE 0: ARCHITECT TERMINAL");
      }
    };
    window.addEventListener('nexorax-trigger-tab-change', handleTriggerTabChange);
    return () => window.removeEventListener('nexorax-trigger-tab-change', handleTriggerTabChange);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const legalPaths = ["/privacy-policy", "/cookie-policy", "/terms", "/refund-policy", "/cancellation-policy", "/cookie-settings", "/accessibility", "/privacy-rights-request", "/contact"];
      if (legalPaths.includes(path)) {
        setActiveLandingTab(path);
      } else if (path === "/pricing") {
        setActiveLandingTab("Tiers");
      } else if (path === "/login") {
        setActiveLandingTab("Login");
      } else if (path === "/signup" || path === "/sign-up") {
        setActiveLandingTab("Join");
      } else if (path === "/services") {
        setActiveLandingTab("Services / Solutions");
      } else if (path === "/marketplace") {
        setActiveLandingTab("NexoraX Marketplace");
      } else if (path === "/digital-products") {
        setActiveLandingTab("Digital Products");
      } else if (path === "/product-builder") {
        setActiveLandingTab("Product Builder");
      } else if (path === "/my-projects") {
        setActiveLandingTab("My Projects");
      } else if (path === "/my-purchases") {
        setActiveLandingTab("My Purchases");
      } else if (path === "/nexus-os") {
        setActiveLandingTab("Nexus OS");
      } else if (path === "/guides-videos") {
        setActiveLandingTab("Guides & Videos");
      } else if (path === "/nexus-search") {
        setActiveLandingTab("Nexus Search");
      } else if (path === "/nexus-hosting") {
        setActiveLandingTab("Nexus Hosting");
      } else if (path === "/nexus-mail-domains") {
        setActiveLandingTab("Nexus Mail & Domains");
      } else if (path === "/nexus-galaxy-lab") {
        setActiveLandingTab("Nexus Galaxy Lab");
      } else if (path === "/operator-console") {
        setActiveLandingTab("Operator Console");
      } else if (path === "/remote-workplace") {
        setActiveLandingTab("Remote Workplace");
      } else if (path === "/admin-control") {
        setActiveLandingTab(isDirector ? "Admin Panel" : "Login");
      } else if (path === "/" || path === "") {
      setActiveLandingTab(null);
    }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Synchronize activeLandingTab state with Browser URL and update SEO metadata
  useEffect(() => {
    if (typeof window === "undefined") return;
    const currentPath = window.location.pathname;
    let targetPath = "/";

    if (!activeLandingTab) {
      targetPath = "/";
    } else if (activeLandingTab === "Services / Solutions") {
      targetPath = "/services";
    } else if (activeLandingTab === "NexoraX Marketplace") {
      targetPath = "/marketplace";
    } else if (activeLandingTab === "Digital Products" || activeLandingTab === "Templates & Apps") {
      targetPath = "/digital-products";
    } else if (activeLandingTab === "Product Builder") {
      targetPath = "/product-builder";
    } else if (activeLandingTab === "My Projects") {
      targetPath = "/my-projects";
    } else if (activeLandingTab === "My Purchases") {
      targetPath = "/my-purchases";
    } else if (activeLandingTab === "Nexus OS") {
      targetPath = "/nexus-os";
    } else if (activeLandingTab === "Operator Console") {
      targetPath = "/operator-console";
    } else if (activeLandingTab === "Remote Workplace") {
      targetPath = "/remote-workplace";
    } else if (activeLandingTab === "Smart Quote") {
      targetPath = "/smart-quote";
    } else if (activeLandingTab === "Client Preview") {
      targetPath = "/client-preview";
    } else if (activeLandingTab === "Showcase") {
      targetPath = "/showcase";
    } else if (activeLandingTab === "QR Campaign") {
      targetPath = "/qr-campaign";
    } else if (activeLandingTab === "Launch Pad") {
      targetPath = "/campaign-launch-pad";
    } else if (activeLandingTab === "Campaign Hub") {
      targetPath = "/campaign-hub";
    } else if (activeLandingTab === "Admin Panel") {
      targetPath = "/admin-control";
    } else if (activeLandingTab === "Guides & Videos" || activeLandingTab === "GuidesAndVideos") {
      targetPath = "/guides-videos";
    } else if (activeLandingTab === "Remote Training") {
      targetPath = "/remote-training";
    } else if (activeLandingTab === "Tiers") {
      targetPath = "/pricing";
    } else if (activeLandingTab === "Login") {
      targetPath = "/login";
    } else if (activeLandingTab === "Join") {
      targetPath = "/signup";
    } else if (activeLandingTab === "Contact" || activeLandingTab === "/contact") {
      targetPath = "/contact";
    } else if (activeLandingTab.startsWith("/")) {
      targetPath = activeLandingTab;
    } else {
      return;
    }

    if (currentPath !== targetPath) {
      if (targetPath === "/remote-workplace") {
        window.location.assign(targetPath);
        return;
      }

      window.history.pushState(null, "", targetPath);
    }

    
// Dynamic SEO Metadata Integration
    let title = "Nexorax Digital OS | NexoraX Multi-Agent Corporate Solutions";
    let description = "Empowering UK enterprises, care facilities, and SMEs with autonomous workflow engines, automated contract acquisition nodes, and strict Regulation 15 grant form-filling software. Engineered by NexoraX Digital Ltd in Dunfermline.";
    
    if (targetPath === "/operator-console") {
      title = "NexoraX Operator Console | Guided Partner Operations Workflow";
      description = "Guided client intake, campaign planning, website brief generation, and product template task queue. Controlled operator workspace.";
    } else if (targetPath === "/remote-workplace") {
      title = "NexoraX Remote Workplace | Team Campaigns Hub";
      description = "Protected workspace hub for NexoraX operators to coordinate Canva tasks, campaign briefs, outreach leads, and client jobs.";
    } else if (targetPath === "/admin-control") {
      title = "NEXORAX ADMIN CONTROL CENTRE | Global Overwatch System";
      description = "Director console to monitor active workforce sessions, audit logs, run isolation tests, and manage operator invitations.";
    } else if (targetPath === "/digital-products") {
      title = "Ready-Made Websites, Apps, Dashboards & AI Workflow Templates | NexoraX Digital";
      description = "Explore NexoraX Digital Products including ready-made websites, mobile app templates, dashboards, slide decks, AI assistants, workflow templates, code packs, and digital asset bundles.";
    } else if (targetPath === "/product-builder") {
      title = "NEXORAX Product Builder | Online No-Code Customizer Tool";
      description = "Customise templates, build brand-aligned layout structures, adjust styling palettes, or upload layout scans inside the official prompt-free NEXORAX system.";
    } else if (targetPath === "/my-projects") {
      title = "My Customized Projects | NexoraX Digital Space";
      description = "Browse and customize your saved digital product drafts, request setups, and manage active web and mobile system development nodes.";
    } else if (targetPath === "/my-purchases") {
      title = "My Licensed Asset Purchases | NexoraX Vault";
      description = "Access bought templates and system licences, check active compliance coverage, and customize assets within NEXORAX Product Builder.";
    } else if (targetPath === "/marketplace") {
      title = "Smart Gadget Marketplace & Future Tech Product Discovery | NexoraX Digital";
      description = "Explore smart gadgets, future tech, product discovery, product video references, supplier research, and technology updates with NexoraX Marketplace.";
    } else if (targetPath === "/pricing") {
      title = "NexoraX Corporate Pricing & Service Tiers | Premium Enterprise Solutions";
      description = "Explore flexible, high-fidelity corporate membership tiers from Bronze to VIP Singularity. Find the perfect resource grant or subscription plan for your organization's digital transformation.";
    } else if (targetPath === "/contact") {
      title = "Secure NexoraX Inquiries | Communications Gateway";
      description = "Establish a direct communication link with the Office of the Director. Secure dispatch logged immediately with 0.8s round-trip telemetry verification.";
    } else if (targetPath === "/guides-videos") {
      title = "NexoraX Guides & Videos | Corporate Video & Technical Walkthroughs";
      description = "Access our comprehensive library of step-by-step guides, operational walkthroughs, and system demonstration videos built to streamline deployment.";
    } else if (targetPath === "/nexus-os") {
      title = "Nexus OS Control Plane | Autonomous Architecture Node";
      description = "Initialize quantum telemetry streams, monitor edge computing channels, and manage administrative clearance levels in real time.";
    } else if (targetPath === "/services") {
      title = "NexoraX Services & Solutions | Enterprise Transformation Stack";
      description = "High-velocity B2B artificial intelligence systems, custom web engineering, programmatically optimized marketing, and elite server deployment.";
    }

    document.title = title;

    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) {
      descMeta.setAttribute("content", description);
    }
    const ogTitleMeta = document.querySelector('meta[property="og:title"]');
    if (ogTitleMeta) {
      ogTitleMeta.setAttribute("content", title);
    }
    const ogDescMeta = document.querySelector('meta[property="og:description"]');
    if (ogDescMeta) {
      ogDescMeta.setAttribute("content", description);
    }
  }, [activeLandingTab]);


  // NEXORAX PUBLIC ROOT GATE FIX
  // Public visitors must see the real homepage first.
  // Login, Tiers, and sprint access windows must not hijack nexoraxdigital.co.uk on initial load.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const path = window.location.pathname;
    const search = window.location.search || "";
    const isPublicRoot = path === "/" || path === "";
    const isExplicitInternalRoute =
      search.includes("admin") ||
      search.includes("app") ||
      search.includes("login") ||
      search.includes("pricing") ||
      path.includes("admin") ||
      path.includes("login") ||
      path.includes("pricing");

    if (!isPublicRoot || isExplicitInternalRoute) return;

    if (
      activeLandingTab === "Login" ||
      activeLandingTab === "Tiers" ||
      vaultPhase === "vault" ||
      vaultPhase === "extraction" ||
      vaultPhase === "plan" ||
      vaultPhase === "locked"
    ) {
      setActiveLandingTab(null);
      setVaultPhase(null);
      setPhaseTimeLeft(null);
    }
  }, [activeLandingTab, vaultPhase]);
  // NEXORAX PUBLIC ROOT GATE FIX END

  const searchResults = React.useMemo(() => {
    if (!globalSearch.trim()) return [];
    const query = globalSearch.toLowerCase();
    const results: Array<{
      type: "PAGE" | "TERMINAL" | "CATEGORY" | "NODE";
      label: string;
      desc: string;
      categoryTitle?: string;
      targetTab: string;
      subnode?: string;
    }> = [];

    // 1. Landing Pages
    const landingPages = [
      { tab: "Home", desc: "Vanguard dashboard, newsfeed & operations matrix" },
      { tab: "Latest News", desc: "Corporate news updates & strategic alerts" },
      { tab: "Updates", desc: "System patch-notes & commit logs" },
      ...(isDirector ? [{ tab: "Admin Panel", desc: "Director admin login portal & secure access" }] : []),
      { tab: "About", desc: "Core lore, corporate doctrine & performance targets" },
      { tab: "Vision", desc: "The NexoraX neural core & deep vision forge" },
      { tab: "Team", desc: "NexoraX active personnel, Swarms & Pakistan Node Sync" },
      { tab: "Guides & Videos", desc: "A total video guide library and promotional animation platform with Website Update Watcher" },
      { tab: "Proof", desc: "Enterprise studies & £1.2M validated capital yields" },
      { tab: "Tiers", desc: "Pricing plans, VIP lifetime membership & 1p query options" },
      { tab: "Charities", desc: "3% Community Allocation, local & global split ledger" },
      { tab: "Points", desc: "Liquidation system, user status & reward router" },
      { tab: "NexoraX Voice", desc: "Cooperative autonomous voice squads & real-time TTS simulation" },
      { tab: "NexoraX Marketplace", desc: "NexoraX luxury checkout portal, node sales, precise Community Allocation calculation & secure payment methods" },
      { tab: "NexoraX QR Dispatcher", desc: "Bulk Generation of Secure QR Codes for 10,000 Nodes under our Community Allocation guidelines" },
      { tab: "NexoraX QR Generator", desc: "Unique, secure QR codes for client invoices, allowing for rapid 5% deposit settlement with unified billing and tax calculations" },
      { tab: "Nexorax Studio", desc: "NexoraX cognitive engine dashboard, model compilers & 15-2-8 minute business development pipelines" },
      { tab: "Proposal Automator", desc: "Node-21 automated architectural proposal compiler with 90+ categories and secure billing gateway routing" },
      { tab: "NexoraX Talent Hub", desc: "Specialised Private Secretariat onboarding, secure onboarding tracker, and neural chat console" },
      { tab: "NexoraX Workspace Suite", desc: "Secure local documents, drive and spreadsheets platform built on high autonomy protocols" },
      { tab: "NexoraX Multiverse Search Engine", desc: "High-speed multi-vector search and deep sector scanning core" },
      { tab: "NexoraX Training Tab", desc: "NexoraX Staff Training Matrix & Pipeline Generator with Temporal secures 60S freeze simulator" },
      { tab: "91-100 Zenith Heuristics", desc: "Protocol NEXORAX_ZENITH_FINALIZATION_V1 containing categories 91 to 100 with advanced quantum and linguistics sub-nodes" },
      { tab: "NexoraX Incubator", desc: "NexoraX Incubator node matrix deployment console with 30-node capability and VIP Access Protocol tracking" },
      { tab: "Category 91", desc: "Category 91 NexoraX Incubator deployment matrix with 30 active automation modules, community allocation configuration & 60s freeze safety triggers" },
      { tab: "NexoraX Master Engine", desc: "NexoraX Master Engine Grand Unified Matrix with 100 blocks x 100 sub-nodes and 10,000-node density" },
      { tab: "NexoraX Hermes Node", desc: "NexoraX Hermes active automated neural outreach simulator with Multi-Vector targets" },
      { tab: "NexoraX Advanced Console", desc: "A high-level command console and real-time ad ecosystem node tracker directly fed by Performance Learning feeds." },
      { tab: "NexoraX AI Agent Automation", desc: "Autonomous multi-agent controller to coordinate learning loops, custom cognitive weight loading, and live telemetry outputs in real-time." },
      { tab: "Nexus Galaxy Lab", desc: "Creative AI playground to design and deploy custom research notebooks, sync with premium payment tiers, and mirror secure notes." },
      { tab: "Mail & Domains Platform", desc: "Secure SMTP/IMAP core workspace to manage support@nexoraxdigital.co.uk and configure DNS edge shunting controls." },
      { tab: "NexoraX Backup Storage Blocks", desc: "NEXORAX BACKUP STORAGE BLOCKS (FIREBASE ECLIPSE): A multi-tenant, bucket-level storage layout utilizing SHA-256 validation monitors, file-type byte meters, and real-time upload speed dials to visually out-perform standard Firebase buckets." },
      { tab: "Unified Mail Ingress Array", desc: "UNIFIED MAIL INGRESS ARRAY (EXCHANGE ECLIPSE): Live pipeline tracks mapping incoming and outgoing SMTP/IMAP servers for support@nexoraxdigital.co.uk and info@nexoraxdigital.co.uk with instant DKIM/SPF cryptography badge verification layouts." },
      { tab: "Domain Registry & Edge Tunnel Monitor", desc: "DOMAIN REGISTRY & EDGE TUNNEL MONITOR (CLOUDFLARE ECLIPSE): Visual representations of active DNS A-Records, MX records, and proxy security status directly linking nexoraxdigital.co.uk across Cloudflare edge caches straight to your German Hetzner bare-metal hardware container layer." },
      { tab: "Nexus Accounting & Metering System", desc: "NEXUS ACCOUNTING & METERING SYSTEM (API_KEY_ENGINE): Two-layer ledger tracking continuous product use parameters, invoices, payment reconciliation, and 3% Community Allocation." },
      { tab: "Login", desc: "Google Auth credential portal" },
      { tab: "Join", desc: "Onboarding matrix & VIP registration form" }
    ];

    landingPages.forEach(p => {
      if (p.tab.toLowerCase().includes(query) || p.desc.toLowerCase().includes(query)) {
        results.push({
          type: "PAGE",
          label: p.tab,
          desc: p.desc,
          targetTab: p.tab
        });
      }
    });

    // 2. Custom Terminals (Active categories mapped explicitly)
    const customTerminals = [
      { tab: "DYNAMIC PAYLOAD MATRIX", desc: "Custom JSON structure sandbox" },
      { tab: "WEBSITE SITE EDITOR", desc: "Proprietary content & website layout sync" },
      { tab: "BRANDING COMMAND", desc: "Audits, archives & active brand protection logs" },
      { tab: "CRM, DASHBOARD & SLIDE DECK", desc: "Customer relationships, client metrics & slides" },
      { tab: "SECRETARY NODE", desc: "Task queue & chief of staff coordinator" },
      { tab: "ROGUE ENTITY ELIMINATION", desc: "Security first-party analytics & hostile domain intercept" },
      { tab: "SPECTACLE COMMAND", desc: "Media controls, presentation modes & video engine" },
      { tab: "NODE 0: ARCHITECT TERMINAL", desc: "The primary high-authority system interface" }
    ];

    customTerminals.forEach(t => {
      if (t.tab.toLowerCase().includes(query) || t.desc.toLowerCase().includes(query)) {
        results.push({
          type: "TERMINAL",
          label: t.tab,
          desc: t.desc,
          targetTab: t.tab
        });
      }
    });

    // 3. Dynamic Categories & Sub-nodes from data index
    const allCategories = content.categories || CATEGORIES || [];
    allCategories.forEach(cat => {
      // Check if category title matches
      if (cat.title.toLowerCase().includes(query)) {
        results.push({
          type: "CATEGORY",
          label: cat.title,
          desc: `Access deep-subnet category with ${cat.nodes.length} subnodes`,
          targetTab: cat.title
        });
      }

      // Check if any subnode inside category matches
      cat.nodes.forEach(node => {
        if (node.toLowerCase().includes(query)) {
          results.push({
            type: "NODE",
            label: node,
            desc: `Sub-node under [ ${cat.title} ] subnet`,
            categoryTitle: cat.title,
            targetTab: cat.title,
            subnode: node
          });
        }
      });
    });

    return results.slice(0, 10); // Limit to top 10 matching metrics
  }, [globalSearch, content.categories]);

  const handleSearchResultClick = (result: any) => {
    setGlobalSearch("");
    setIsSearchFocused(false);
    
    // Core Navigation Logic
    if (result.type === "PAGE") {
      setActiveLandingTab(result.targetTab);
      setActiveMainTab("NODE 0: ARCHITECT TERMINAL");
      setActiveSubTab(null);
    } else {
      setActiveLandingTab(null);
      setActiveMainTab(result.targetTab);
      if (result.subnode) {
        setActiveSubTab(result.subnode);
      } else {
        setActiveSubTab(null);
      }
    }
  };

  useEffect(() => {
    setCategoryOrder(
      (content.categories || CATEGORIES).filter(
        cat => !REMOVED_CATEGORIES.includes(cat.title)
      )
    );
  }, [content.categories]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleMouseEnterDesc = (id: number) => {
    if (dropdownTimeout.current) window.clearTimeout(dropdownTimeout.current);
    setActiveDropdown(id);
  };

  const handleMouseLeaveDesc = () => {
    dropdownTimeout.current = window.setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  useEffect(() => {
    if (phaseTimeLeft === null || !isVaultUnlocked) return;

    if (phaseTimeLeft <= 0) {
      if (vaultPhase === "vault") {
        setVaultPhase("extraction");
        setPhaseTimeLeft(2 * 60);
      } else if (vaultPhase === "extraction") {
        setIsVaultUnlocked(false);
        setVaultPhase(null);
        setPhaseTimeLeft(null);
      } else if (vaultPhase === "plan") {
        setVaultPhase("locked");
        setPhaseTimeLeft(null);
      }
      return;
    }

    const timer = setInterval(() => {
      setPhaseTimeLeft((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => clearInterval(timer);
  }, [phaseTimeLeft, isVaultUnlocked, vaultPhase]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // Zero-Extraction Protocol (Data NexoraXty Shield)
  useEffect(() => {
    if (typeof (window as any).runSecurityCloakingCheck === 'function') {
      (window as any).runSecurityCloakingCheck();
    }
  }, [isVaultUnlocked]);

  // Zero-Extraction Protocol (Data NexoraXty Shield)
  useEffect(() => {
    if (!isVaultUnlocked) return;

    const triggerTermination = () => {
      setIsSecViolation(true);
      setPhaseTimeLeft(null); // Kill God-Mode countdown
      setTimeout(() => {
        setIsSecViolation(false);
        setIsVaultUnlocked(false);
        setVaultPhase(null);
        setActiveLandingTab(null);
      }, 3000);
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      const key = e.key.toLowerCase();

      const isSave = cmdOrCtrl && key === "s";
      const isSource = cmdOrCtrl && key === "u";
      const isInspect =
        (cmdOrCtrl && e.shiftKey && (key === "i" || key === "c")) ||
        (isMac && e.metaKey && e.altKey && key === "i");
      const isF12 = e.key === "F12";
      const isPrintScreen = e.key === "PrintScreen";
      const isMacScreenshot =
        isMac &&
        e.metaKey &&
        e.shiftKey &&
        (key === "3" || key === "4" || key === "5");
      const isWinScreenshot = e.metaKey && e.shiftKey && key === "s";

      if (
        isSave ||
        isSource ||
        isInspect ||
        isF12 ||
        isPrintScreen ||
        isMacScreenshot ||
        isWinScreenshot
      ) {
        e.preventDefault();
        triggerTermination();
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsBlurred(true);
      } else {
        setIsBlurred(false);
      }
    };

    const handleBlur = () => setIsBlurred(true);
    const handleFocus = () => setIsBlurred(false);

    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isVaultUnlocked]);

  // Auto-logout inactivity timer (15 minutes)
  useEffect(() => {
    if (!isVaultUnlocked || isDirector) return;

    let timeoutId: any;

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.warn("[SECURITY DISPATCH] 15 minutes of inactivity detected. Enforcing NexoraX Auto-Logout Protocol.");
        
        // Remove admin signature token and other session states
        sessionStorage.removeItem('admin session verification');
        sessionStorage.removeItem("nexorax_mfa_verified");
        sessionStorage.removeItem("nexorax_bypassed_user_email");
        
        // Reset local States to fully revoke administrative privilege
        setIsVaultUnlocked(false);
        setVaultPhase(null);
        setPhaseTimeLeft(null);
      }, 15 * 60 * 1000); // 15 minutes in milliseconds
    };

    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    // Initialize timer immediately on mount/unlock
    resetTimer();

    // Attach listeners across window
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isVaultUnlocked]);

  // Global event listener for bottom/footer logout actions
  useEffect(() => {
    const handleGlobalLogoutEvent = () => {
      setMfaVerified(false);
      localStorage.removeItem("nexorax_director_session");
      showToast("Node session successfully severed. Safely disconnected active channels.", "info");
      setActiveLandingTab("Login");
    };
    window.addEventListener("nexorax-global-logout", handleGlobalLogoutEvent);
    return () => {
      window.removeEventListener("nexorax-global-logout", handleGlobalLogoutEvent);
    };
  }, []);

  // Mandatory Runtime Monitoring Deck Boot sequence logging
  useEffect(() => {
    console.log(
      "%c[SYSTEM ARCHITECT VALIDATED] The NexoraX Protocol is Online.",
      "color: #00F0FF; font-weight: bold; font-size: 14px;",
    );
    console.log(
      "%cSecure Invoice Register Verified",
      "color: #7000FF; font-family: monospace;",
    );

    // Deep link routing check for product pages (?pdp=something)
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const pdp = params.get("pdp");
      if (pdp) {
        setActiveLandingTab("NexoraX Marketplace");
        setActiveMainTab("NODE 0: ARCHITECT TERMINAL");
        setActiveSubTab(null);
        console.log(`[DEEP LINK ACTIVE] Directed to NexoraX Marketplace Node PDP: ${pdp}`);
      }
    }
  }, []);

  const handleLogin = async () => {
    // Auth logic
    if (!user) {
      try {
        await signInWithGoogle();
      } catch (err) {
        console.error("Auth failed", err);
      }
    }

    setIsVaultUnlocked(true);
    setVaultPhase("vault");
    setPhaseTimeLeft(15 * 60);
  };

  const handleLoginSuccess = (email?: string) => {
    // Successful customer authentication unlocks the public website.
    // Remote Workplace remains available only through its dedicated tab.
    setIsVaultUnlocked(true);
    setVaultPhase("vault");
    setPhaseTimeLeft(15 * 60);
    setActiveLandingTab("Home");
    setActiveMainTab("NODE 0: ARCHITECT TERMINAL");
    setActiveSubTab(null);

    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", "/");
    }
  };

  return (
    <div className="h-screen w-full bg-[#020202] text-slate-300 font-sans flex flex-col overflow-hidden relative selection:bg-cyan-500/30 selection:text-cyan-50">
      {isDirector && (
        <div className="w-full bg-[#050914] border-b border-purple-500/20 px-6 py-2 flex justify-between items-center text-[10px] font-mono text-purple-400 select-none shadow-[0_4px_10px_rgba(0,0,0,0.3)] z-[1000] shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-purple-500 rounded-full shrink-0 animate-ping" />
            <span>DIRECTOR ADMIN PANEL — PERSISTENT SESSION ACTIVE</span>
          </div>
          <button
            onClick={handleHeaderLogout}
            style={{ cursor: 'pointer' }}
            className="px-2 py-0.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded font-bold uppercase text-[8.5px] transition-all cursor-pointer"
          >
            Logout
          </button>
        </div>
      )}
      {/* Neural Video Background */}
      <video
        ref={backgroundVideoRef}
        key={currentVideoSrc}
        src={currentVideoSrc}
        autoPlay={!videoPaused}
        loop={!videoAutoplay}
        muted
        playsInline
        preload="metadata"
        className="fixed inset-0 w-full h-full object-cover -z-50"
        style={{
          filter: getAppVideoFilter(),
          opacity: videoOpacity * 0.70 * videoTransitionOpacity,
          transition: `opacity ${videoTransitionDuration}ms ease-in-out, filter ${videoTransitionDuration}ms ease-in-out`
        }}
        poster="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2070"
        onError={(e) => {
          console.warn("Background video could not be loaded");
          e.currentTarget.style.display = "none";
        }}
        onCanPlay={(e) => {
          if (!videoPaused) {
            const playPromise = e.currentTarget.play();
            if (playPromise !== undefined) {
               playPromise.catch(err => {
                   console.warn("Video autoPlay failed:", err);
                });
            }
          } else {
            e.currentTarget.pause();
          }
        }}
        onEnded={() => {
          if (videoAutoplay) {
            const bgVideos = ["/brain1.mp4", "/brain2.mp4", "/brain4.mp4", "/brain9.mp4", "/brain10.mp4", "/words1.mp4"];
            const index = bgVideos.indexOf(currentVideoSrc);
            const nextIndex = (index + 1) % bgVideos.length;
            transitionToVideoRef.current(bgVideos[nextIndex]);
            window.dispatchEvent(new CustomEvent('nexorax-video-cycle', { detail: bgVideos[nextIndex] }));
          }
        }}
      />
      {/* Cinematic Dark Overlays (Task 3) */}
      <div 
        className="fixed inset-0 -z-45 pointer-events-none"
        style={{
          background: `
            linear-gradient(90deg, rgba(3, 0, 12, 0.82), rgba(8, 0, 24, 0.72), rgba(3, 0, 12, 0.84))
          `
        }}
      />
      <div 
        className="fixed inset-0 -z-44 pointer-events-none"
        style={{
          background: `
            linear-gradient(180deg, rgba(0, 0, 0, 0.65), rgba(5, 0, 18, 0.45), rgba(0, 0, 0, 0.72))
          `
        }}
      />
      <div 
        className="fixed inset-0 -z-43 pointer-events-none opacity-80"
        style={{
          background: `
            radial-gradient(circle at 25% 20%, rgba(168, 85, 247, 0.15), transparent 35%),
            radial-gradient(circle at 75% 40%, rgba(34, 211, 238, 0.10), transparent 38%)
          `
        }}
      />
      <div className="fixed inset-0 bg-[#020202]/10 -z-40 pointer-events-none" />

      {/* Dynamic Background Grid and Glow Effects */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 2px 2px, rgba(122,0,255,0.2) 1px, transparent 0)",
          backgroundSize: "40px 40px",
          opacity: gridOpacity,
        }}
      />
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#00f0ff]/10 blur-[120px] rounded-full pointer-events-none"></div>

      {showArchitectVoice && (
        <ArchitectVoiceModal 
          onClose={() => setShowArchitectVoice(false)} 
          initialConcierge={voiceAssistantMode === 'concierge'}
        />
      )}

      <NexoraXConciergeDock
        enabled={!isAppLoading && activeLandingTab === "Home" && !showArchitectVoice}
        onNavigate={(tab) => {
          setActiveLandingTab(tab);
          setActiveMainTab("NODE 0: ARCHITECT TERMINAL");
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onOpenFullConcierge={() => {
          setVoiceAssistantMode('concierge');
          setShowArchitectVoice(true);
        }}
      />

      {/* Persistent top-aligned NexoraX Ledger Ticker */}
      {isVaultUnlocked && <NexoraXLedgerTicker />}

      <AnimatePresence>
        {isAppLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#010101]/95 text-slate-300 select-none overflow-hidden"
          >
            {/* Ambient cyber glow elements */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-[120px] pointer-events-none" />
            
            {/* Cyber Grid pattern */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-40"
              style={{
                backgroundImage: "linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06))",
                backgroundSize: "100% 4px, 6px 100%"
              }}
            />

            <div className="w-full max-w-2xl px-6 flex flex-col items-center">
              {/* Rotating main logo emblem */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 12, ease: "linear" }}
                className="relative mb-8"
              >
                <div className="w-24 h-24 rounded-full border border-purple-500/20 flex items-center justify-center bg-[#020202]/80 shadow-[0_0_30px_rgba(168,85,247,0.15)]">
                  <div className="w-18 h-18 rounded-full border border-dashed border-cyan-500/40 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]" />
                  </div>
                </div>
                {/* Orbital tech-nodule widgets */}
                <span className="absolute -top-1 -left-1 w-3 h-3 rounded-full bg-cyan-500/80 animate-ping" />
                <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-purple-500/80" />
              </motion.div>

              {/* Title & Institutional Identity */}
              <div className="text-center space-y-1 mb-8">
                <div className="flex items-center justify-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-400 animate-pulse" />
                  <span className="font-mono text-xs text-cyan-400/80 tracking-[0.25em]">NexoraX Node Verification</span>
                </div>
                <h1 className="text-2xl font-bold tracking-[0.15em] text-white">NEXORAX CORE OS</h1>
                <p className="text-[10px] font-mono text-slate-500 uppercase">OFFICE OF THE DIRECTOR / Lead Director</p>
              </div>

              {/* Loader Segment Bar & Percent */}
              <div className="w-full bg-[#080808]/80 border border-slate-800 rounded-lg p-5 space-y-4 shadow-[0_4px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
                <div className="flex items-center justify-between text-xs font-mono">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-3.5 h-3.5 text-cyan-500" />
                    <span>EPYC-128C // ARCHITECT_BOOT</span>
                  </div>
                  <span className="text-cyan-400 font-bold">{loadingProgress}%</span>
                </div>

                <div className="relative h-2 bg-[#121212] border border-slate-800 rounded overflow-hidden flex gap-0.5 p-[2px]">
                  {/* Dynamic split segmented notches */}
                  {Array.from({ length: 20 }).map((_, i) => {
                    const threshold = (i / 20) * 100;
                    const isActive = loadingProgress >= threshold;
                    return (
                      <div
                        key={i}
                        className={`h-full flex-1 transition-all duration-300 ${
                          isActive 
                            ? i < 8 
                              ? "bg-cyan-500/80 shadow-[0_0_4px_rgba(6,182,212,0.8)]" 
                              : i < 15 
                                ? "bg-purple-500/80 shadow-[0_0_4px_rgba(168,85,247,0.8)]" 
                                : "bg-fuchsia-500/80 shadow-[0_0_4px_rgba(217,70,239,0.8)]"
                            : "bg-[#181818]"
                        }`}
                      />
                    );
                  })}
                </div>

                <div className="h-6 flex items-center justify-start overflow-hidden">
                  <motion.p
                    key={loadingStep}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="font-mono text-[11px] text-slate-400 tracking-wide truncate"
                  >
                    &gt;_ {loadingSteps[loadingStep]}
                  </motion.p>
                </div>
              </div>

              {/* Diagnostic Log Telemetry box */}
              <div className="w-full mt-4 bg-black/40 border border-slate-900 rounded-xl p-3.5 font-mono text-[9px] text-slate-500 space-y-1.5 shadow-inner">
                <div className="flex justify-between border-b border-slate-900 pb-1.5 mb-2 text-[10px] text-slate-400">
                  <span>CORE INITIALIZATION</span>
                  <span className="text-emerald-500 font-semibold animate-pulse">SYSTEM SECURE</span>
                </div>
                <div className="flex justify-between">
                  <span>NEXORAX_ROUTING_INDEX:</span>
                  <span className="text-slate-400">@nexorax.co.uk VERIFIED</span>
                </div>
                <div className="flex justify-between">
                  <span>ENCRYPTION_DEFENSE_BARRIER:</span>
                  <span className="text-slate-400">SHIELD-02 ACTIVE</span>
                </div>
                <div className="flex justify-between text-cyan-400/70">
                  <span>BILLING_CONFIGURATION:</span>
                  <span>SECURE_PAYMENT_PROVIDER_CONNECTED</span>
                </div>
              </div>

              {/* Secure fast-track bypass trigger */}
              <div className="mt-8 flex flex-col items-center gap-1">
                <button
                  type="button"
                  onClick={() => setIsAppLoading(false)}
                  className="px-5 py-2.5 rounded-xl border border-slate-800 text-slate-300 text-[10.5px] font-mono tracking-widest uppercase bg-[#050505] hover:bg-white hover:text-black hover:border-white transition-all duration-300 flex items-center gap-2 shadow-lg cursor-pointer hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] active:scale-97"
                >
                  <Terminal className="w-3.5 h-3.5" />
                  INITIALIZE NEXORAX TERMINAL
                </button>
                <span className="text-[9px] text-slate-600 font-mono mt-1">NexoraX Corporate Protocol Node-09.2</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {!isVaultUnlocked || activeLandingTab !== null ? (
          // DIMENSION 1: LANDING PAGE
          <motion.div
            key="dimension1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="flex flex-col w-full h-full z-10 overflow-y-auto custom-scrollbar"
          >
            <NexoraXHeaderNavigation 
              isVaultUnlocked={isVaultUnlocked}
              onLocalTabChange={setActiveLandingTab} 
              activeTab={activeLandingTab} 
              onSelectCategory={(categoryTitle, subNode) => {
                setActiveLandingTab(null);
                setActiveMainTab(categoryTitle);
                setActiveSubTab(subNode || null);
                const category = categoryOrder.find(
                  (c) => c.title === categoryTitle,
                );
                if (category) {
                  setActiveDropdown(category.id);
                }
              }}
              user={user}
              handleHeaderLogout={handleHeaderLogout}
              globalSearch={globalSearch}
              setGlobalSearch={setGlobalSearch}
              searchResults={searchResults}
              isSearchFocused={isSearchFocused}
              setIsSearchFocused={setIsSearchFocused}
              handleSearchResultClick={handleSearchResultClick}
              currentTime={currentTime}
              onShowGovCorpGateway={() => {
                sessionStorage.setItem("gov_corp_mode", "true");
                window.dispatchEvent(new Event("gov-corp-mode-changed"));
                setActiveLandingTab("Login");
              }}
              companyDivisions={companyDivisions && companyDivisions.length > 0 ? companyDivisions : (content.companyDivisions || COMPANY_DIVISIONS)}
            />



            {/* Central Section */}
            <main
              className={`flex-1 flex flex-col p-8 ${activeMainTab !== "NODE 0: ARCHITECT TERMINAL" || activeLandingTab ? "py-8 max-w-[1600px]" : "py-20 text-center max-w-6xl space-y-24"} min-h-[max-content] w-full mx-auto`}
            >
              {activeLandingTab ? (
                <motion.div
                  key={activeLandingTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full flex justify-center"
                >
                  {["/privacy-policy", "/cookie-policy", "/terms", "/refund-policy", "/cancellation-policy", "/cookie-settings", "/accessibility", "/privacy-rights-request", "/contact"].includes(activeLandingTab) ? (
                    <NexoraXLegalDocuments currentRoute={activeLandingTab} onNavigate={setActiveLandingTab} />
                  ) : activeLandingTab === "Reality Audit (V.1)" ? (
                    <RealityAuditTerminal />
                  ) : activeLandingTab === "Nexorax Chat" ? (
                    <NexoraxChat />
                  ) : (activeLandingTab === "Operator Onboarding" || activeLandingTab === "Join") ? (
                    <NexoraXOperatorConsole userRole={adminRole} userEmail={user?.email} initialMode="onboarding" />
                  ) : activeLandingTab === "Operator Login" ? (
                    <NexoraXOperatorConsole userRole={adminRole} userEmail={user?.email} initialMode="login" />
                  ) : activeLandingTab === "Operator Console" ? (
                    <NexoraXOperatorConsole userRole={adminRole} userEmail={user?.email} initialMode="console" />
                  ) : activeLandingTab === "Remote Workplace" ? (
                    <NexoraXRemoteWorkplace />
                  ) : activeLandingTab === "Smart Quote" ? (
                    <NexoraXSmartQuote />
                  ) : activeLandingTab === "Campaign Hub" ? (
                <NexoraXCampaignHub />
              ) : activeLandingTab === "Nexus OS" ? (
                    <NexusControlPlane
                      initialUser={user || { email: 'client@nexorax.co.uk', displayName: 'NexoraX Client Representative', plan: 'Bronze' }}
                      mfaVerified={mfaVerified}
                      isDirectorUser={isDirector}
                    />
                  ) : (activeLandingTab === "Grant Funding Pack" || activeLandingTab === "Admin Panel") ? (
                    (!isDirector && activeLandingTab === "Admin Panel") ? (
                      <div className="flex flex-col items-center justify-center h-[70vh] text-center p-6 bg-slate-950/80 rounded-2xl border border-red-500/30 backdrop-blur-md">
                        <span className="text-5xl mb-4">🚫</span>
                        <h2 className="text-2xl font-bold text-red-500 mb-2">ACCESS DENIED</h2>
                        <p className="text-slate-400 max-w-md">This area is reserved for NexoraX Directors and Administrators only. Unauthorized access is monitored.</p>
                      </div>
                    ) : (
                      <AdminControlSafeFallback />
                    )
                  ) : (
                    <CosmicLandingTabPanel
                      title={activeLandingTab}
                      companySectors={companyDivisions && companyDivisions.length > 0 ? companyDivisions : (content.companyDivisions || COMPANY_DIVISIONS)}
                      onShowFiscalStrike={() => setShowFiscalStrike(true)}
                      onShowLegalStrike={() => setShowLegalStrike(true)}
                      onShowArchitectVoice={(mode?: 'standard' | 'concierge') => {
                        setVoiceAssistantMode(mode || 'standard');
                        setShowArchitectVoice(true);
                      }}
                      onLoginSuccess={handleLoginSuccess}
                      onNavigateLandingTab={setActiveLandingTab}
                      onSelectCategory={(categoryTitle) => {
                        setActiveLandingTab(null);
                        setActiveMainTab(categoryTitle);
                        setActiveSubTab(null);
                        const category = categoryOrder.find(
                          (c) => c.title === categoryTitle,
                        );
                        if (category) {
                          setActiveDropdown(category.id);
                        }
                      }}
                      user={user || { email: 'client@nexorax.co.uk', displayName: 'NexoraX Client Representative', plan: 'Bronze' }}
                      isAdmin={isDirector}
                      onAddLog={(module, action, status, details) => {
                        console.log(`[${status}] ${module}: ${action} - ${details}`);
                        const savedLogs = localStorage.getItem('nexorax_audit_logs');
                        const logsArr = savedLogs ? JSON.parse(savedLogs) : [];
                        logsArr.unshift({
                          id: `log-${Date.now()}`,
                          timestamp: new Date().toISOString(),
                          module,
                          action,
                          status,
                          details
                        });
                        localStorage.setItem('nexorax_audit_logs', JSON.stringify(logsArr.slice(0, 50)));
                      }}
                      onAddTaskToWorkSuite={(title, priority) => {
                        console.log(`[TASK MATRIX] Task created: "${title}" - priority: ${priority}`);
                        const savedTasks = localStorage.getItem('nexorax_control_plane_tasks');
                        const tasksArr = savedTasks ? JSON.parse(savedTasks) : [];
                        tasksArr.unshift({
                          id: `task-${Date.now()}`,
                          title,
                          project: 'Digital Setup',
                          client: user?.displayName || 'NexoraX Client',
                          priority,
                          status: 'TODO'
                        });
                        localStorage.setItem('nexorax_control_plane_tasks', JSON.stringify(tasksArr));
                        showToast(`WorkSuite PM task "${title}" generated!`, "success");
                      }}
                    />
                  )}
                </motion.div>
              ) : (activeMainTab && activeMainTab !== "NexoraX Home") ? (
                <motion.div
                  key={activeMainTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-[85vh] min-h-[700px] max-w-[1600px] mx-auto text-left flex flex-col"
                >
                  {activeMainTab === "NODE 0: ARCHITECT TERMINAL" ? (
                    isDirector ? <Node0Terminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "DYNAMIC PAYLOAD MATRIX" ? (
                    isDirector ? <PayloadMatrix /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "WEBSITE SITE EDITOR" ? (
                    isDirector ? <SiteEditor /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "BRANDING COMMAND" ? (
                    isDirector ? <BrandingTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "CRM, DASHBOARD & SLIDE DECK" ? (
                    isDirector ? <CrmDashboardTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "SECRETARY NODE" ? (
                    isDirector ? (
                      <SecretaryNode onNavigate={(tab, subTab) => {
                        setActiveLandingTab(null);
                        setActiveMainTab(tab);
                        setActiveSubTab(subTab || null);
                        setActiveDropdown(null);
                      }} />
                    ) : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "REALITY AUDIT LEDGER" ? (
                    isDirector ? <RealityAuditTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "ROGUE ENTITY ELIMINATION" ? (
                    isDirector ? <RogueEntityTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "SPECTACLE COMMAND" ? (
                    isDirector ? <SpectacleCommandTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : activeMainTab === "Company Divisions" ? (
                    isDirector ? (
                      <CosmicLandingTabPanel
                        title="Company Divisions"
                        onSelectCategory={(categoryTitle) => {
                          setActiveLandingTab(null);
                          setActiveMainTab(categoryTitle);
                          setActiveSubTab(null);
                          const category = categoryOrder.find(
                            (c) => c.title === categoryTitle,
                          );
                          if (category) {
                            setActiveDropdown(category.id);
                          }
                        }}
                      />
                    ) : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  ) : (
                    isDirector ? (
                      <CosmicTabPanel
                        title={activeMainTab}
                        defaultSelectedNode={activeSubTab}
                      />
                    ) : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                  )}
                </motion.div>
              ) : (
                <>
                  {/* Hero Section */}
                  <section className="flex flex-col items-center justify-center space-y-10">
                    <div className="space-y-4">
                      <h1 className="text-5xl md:text-7xl font-sans font-bold text-white tracking-tighter uppercase glow-text-cyan drop-shadow-2xl">
                        {content.heroPrimaryHook}
                      </h1>
                      <p className="text-xl md:text-2xl font-mono text-cyan-400/80 tracking-[0.2em] shadow-black drop-shadow uppercase">
                        {content.heroSubHook}
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center w-full max-w-2xl px-4">
                      <div className="flex-1 glass-panel border border-cyan-500/30 rounded-xl p-8 flex flex-col items-center justify-center shadow-[0_0_30px_rgba(0,240,255,0.1)] transition-transform hover:-translate-y-1">
                        <span className="text-4xl font-bold font-sans tracking-tight text-white glow-text-cyan">
                          20+
                        </span>
                        <span className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-2">
                          Projects
                        </span>
                      </div>
                      <div className="flex-1 glass-panel border border-indigo-500/30 rounded-xl p-8 flex flex-col items-center justify-center shadow-[0_0_30px_rgba(112,0,255,0.1)] transition-transform hover:-translate-y-1">
                        <span className="text-4xl font-bold font-sans tracking-tight text-white text-indigo-100 drop-shadow-[0_0_15px_rgba(112,0,255,0.6)]">
                          £1.2M+
                        </span>
                        <span className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-2">
                          Yield Generated
                        </span>
                      </div>
                    </div>
                  </section>
                  
                  {/* CENTRAL OUT-OF-THIS-WORLD NEXORAX MULTIVERSE SEARCH BENCH */}
                  <section className="w-full max-w-5xl mx-auto px-4">
                    <NexoraxSearchEngine />
                  </section>

                  {/* OUT-OF-THIS-UNIVERSE NEXORAX OMNIPOTENT NEXORAX WORKSPACE (DOCS/DRIVE/OFFICE) */}
                  <section className="w-full max-w-5xl mx-auto px-4">
                    <div className="relative overflow-hidden rounded-3xl border border-indigo-500/20 bg-gradient-to-br from-[#02050f] via-[#050b1a] to-black p-8 shadow-[0_0_50px_rgba(232,64,243,0.08)] border-t-2 border-t-[#e840f3]/50">
                      
                      <div className="relative z-10 space-y-6">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-white/5 pb-4">
                          <div>
                            <div className="flex items-center gap-1.5 text-[#f574fe] font-sans text-[10px] font-black uppercase tracking-[0.25em]">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#f574fe] animate-ping"></span>
                              <span>Local Corporate Autonomy Sector</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-sans font-black text-white uppercase tracking-tight">
                              NexoraX NexoraX Workspace Suite
                            </h2>
                            <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                              100x More Secure, Compliant, and Powerful than Google Drive, Google Docs, & Microsoft Office 365.
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2 bg-black py-1 px-3 border border-indigo-500/20 rounded-xl">
                            <span className="w-2 w-2 h-2 rounded-full bg-emerald-400"></span>
                            <span className="text-[9.5px] font-mono text-[#00c3ff]">ZERO CLOUD MONOPOLY RELIANCE</span>
                          </div>
                        </div>

                        <p className="text-xs font-sans text-slate-300 leading-relaxed">
                          Say farewell to Microsoft Office 365 and Google Drive. Protect your intellectual property from third-party monitoring or censorship. Operate entirely inside NexoraX Digital Ltd's (SC887095) NexoraX Local Cognitive system: <strong>NexoraX Drive</strong> (102 Cloud Archive), <strong>Oracle Docs</strong> (Word processing with NexoraXty Autogen), <strong>Chronos Sheets</strong> (continuous ledger accounting), and <strong>Empire Slides</strong>.
                        </p>

                        <div className="border border-white/5 rounded-2xl bg-black/40 overflow-hidden">
                          <NexoraxWorkspace />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* OUT-OF-THIS-UNIVERSE NEXORAX MULTI-MODEL CHAT PROMOTIONAL BANNER */}
                  <section className="w-full max-w-5xl mx-auto px-4 select-none">
                    <div className="relative rounded-3xl border border-indigo-500/20 bg-gradient-to-r from-[#03081e] via-[#0b0f2a] to-[#03081e] p-8 overflow-hidden group shadow-[0_0_50px_rgba(99,102,241,0.12)] border-t-2 border-t-indigo-500/40">
                      {/* Interactive warp elements */}
                      <div className="absolute inset-0 bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px] opacity-15 pointer-events-none"></div>
                      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#e840f3]/5 rounded-full blur-[120px] pointer-events-none group-hover:bg-[#e840f3]/8 transition-all duration-700"></div>
                      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#00c3ff]/5 rounded-full blur-[120px] pointer-events-none group-hover:bg-[#00c3ff]/8 transition-all duration-700"></div>

                      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="flex-1 text-center md:text-left space-y-4">
                          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 text-sans text-[10px] font-black uppercase tracking-widest text-[#f574fe]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#f574fe] animate-pulse"></span>
                            <span>NexoraX Neural Grid v2.1 Activated</span>
                            <span className="bg-indigo-950/60 text-indigo-400 border border-indigo-500/30 px-1.5 py-0.5 rounded text-[8px]">8 Independent Nodes</span>
                          </div>
                          
                          <h2 className="text-3xl md:text-4xl font-sans font-extrabold tracking-tighter text-white uppercase leading-none">
                            NexoraX Chat Core
                          </h2>
                          
                          <p className="text-xs md:text-sm font-sans text-slate-300 leading-relaxed max-w-xl">
                            Our fully independent, NexoraX cognitive grid carrying the combined capability of all legacy external models, performing <strong className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-300 to-amber-300">100 times better</strong> through our integrated, state-of-the-art Local Memory Vault.
                          </p>

                          <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 pt-1 text-[11px] font-mono text-slate-400 uppercase">
                            <div className="flex items-center gap-1.5 tooltip-box">
                              <span className="text-indigo-400">●</span>
                              <span>SECURE PAYMENT PLATFORM</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-emerald-500">●</span>
                              <span>COMMUNITY ALLOCATION COMPLIANT</span>
                            </div>
                          </div>
                        </div>

                        {/* Interactive prompt button box */}
                        <div className="text-center md:text-right shrink-0 flex flex-col items-center md:items-end gap-3 bg-[#02050e]/60 border border-zinc-800/40 p-5 rounded-2xl md:min-w-[250px]">
                          <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest font-black">standard fixed rate</span>
                          <span className="text-4xl font-sans font-black text-transparent bg-clip-text bg-gradient-to-r from-[#e840f3] to-cyan-400 leading-none">
                            1p / prompt
                          </span>
                          <button
                            onClick={() => {
                              setActiveLandingTab("Nexorax Chat");
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="bg-gradient-to-r from-indigo-600 via-purple-600 to-[#e840f3] hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest px-6 py-3 rounded-xl transition-all hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(112,0,255,0.45)] cursor-pointer select-none"
                          >
                            Access Chat Core
                          </button>
                          <span className="text-[8px] font-mono text-slate-600 italic select-none">No subscription required</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* High Performance Cloud and Category 27 Automation Highlight */}
                  <NexoraxCloudHighlight />

                  {/* About Section & Team */}
                  <section className="space-y-12">
                    <div className="text-center space-y-4">
                      <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight uppercase nexorax-text-style">
                        About NexoraX Digital LTD
                      </h2>
                      <div className="w-16 h-1 bg-cyan-500 mx-auto rounded-full glow-border-cyan"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div className="glass-panel border border-white/10 rounded-2xl p-6 flex flex-col items-center text-center group hover:border-cyan-500/30 transition-all">
                        <div className="w-24 h-24 rounded-full bg-cyan-900/30 border-2 border-cyan-500/50 mb-4 flex items-center justify-center uppercase font-mono text-2xl text-cyan-400 shadow-[0_0_20px_rgba(0,240,255,0.2)]">
                          MS
                        </div>
                        <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
                          Lead Director
                        </h3>
                        <span className="text-xs font-mono text-cyan-500/70 uppercase tracking-widest mt-1">
                          Director & Founder
                        </span>
                        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed font-mono">
                          Lead Architect of the NexoraX System. Directed £150,000
                          Vault.
                        </p>
                      </div>
                      <div className="glass-panel border border-white/10 rounded-2xl p-6 flex flex-col items-center text-center group hover:border-indigo-500/30 transition-all">
                        <div className="w-24 h-24 rounded-full bg-indigo-900/30 border-2 border-indigo-500/50 mb-4 flex items-center justify-center uppercase font-mono text-2xl text-indigo-400">
                          A
                        </div>
                        <h3 className="text-xl font-bold text-white group-hover:text-indigo-400 transition-colors">
                          Staff Coordinator
                        </h3>
                        <span className="text-xs font-mono text-indigo-500/70 uppercase tracking-widest mt-1">
                          Operations
                        </span>
                        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed font-mono">
                          Operations execution and logistics node management.
                        </p>
                      </div>
                      <div className="glass-panel border border-white/10 rounded-2xl p-6 flex flex-col items-center text-center group hover:border-purple-500/30 transition-all">
                        <div className="w-24 h-24 rounded-full bg-purple-900/30 border-2 border-purple-500/50 mb-4 flex items-center justify-center uppercase font-mono text-2xl text-purple-400">
                          N
                        </div>
                        <h3 className="text-xl font-bold text-white group-hover:text-purple-400 transition-colors">
                          Communications Associate
                        </h3>
                        <span className="text-xs font-mono text-purple-500/70 uppercase tracking-widest mt-1">
                          Communications
                        </span>
                        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed font-mono">
                          Brand NexoraXty and elite client communication node.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Pricing / Tiers */}
                  <section className="flex justify-center flex-col items-center">
                    <div className="glass-panel border border-emerald-500/30 rounded-3xl p-10 max-w-2xl w-full text-center space-y-6 relative overflow-hidden group">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                      <h2 className="text-2xl font-bold font-sans uppercase tracking-tight text-white mb-2 relative z-10 glow-text-cyan">
                        1P NexoraX Economic Model
                      </h2>
                      <div className="inline-block px-4 py-1.5 border border-emerald-500/50 rounded-full bg-emerald-900/20 text-emerald-400 text-[10px] font-mono tracking-widest uppercase relative z-10">
                        Global License Standard
                      </div>
                      <div className="py-6 relative z-10 flex flex-col items-center">
                        <span className="text-5xl font-sans font-bold tracking-tighter text-white drop-shadow-[0_0_15px_rgba(16,185,129,0.5)]">
                          £2,499
                        </span>
                        <span className="text-xs font-mono text-slate-500 uppercase tracking-widest mt-2">
                          / month
                        </span>
                      </div>
                      <ul className="text-left font-mono text-xs text-slate-300 space-y-3 max-w-sm mx-auto relative z-10 opacity-80">
                        <li className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></div>{" "}
                          Full Node Access
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></div>{" "}
                          ROI Simulation Rights
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></div>{" "}
                          Sentinel Pipeline
                        </li>
                      </ul>
                    </div>
                  </section>
                </>
              )}
            </main>

            {/* High-Density Footer */}
            <footer className="border-t border-white/5 bg-black/90 p-8 md:p-12 shrink-0 relative mt-auto space-y-12">
              <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
                {/* Brand & Socials */}
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-4 mb-2">
                      <img
                        src="/logo.png?v=2"
                        alt="NexoraX"
                        className="h-10 w-auto drop-shadow-[0_0_15px_rgba(0,240,255,0.8)]"
                      />
                      <span className="font-sans font-bold text-sm md:text-base tracking-widest logo-gradient-text no-sweep drop-shadow-[0_0_8px_rgba(0,240,255,0.5)]">
                        NEXORAX DIGITAL LTD
                      </span>
                    </div>
                    <div className="text-slate-400 font-mono text-[10px] tracking-widest pl-[3.5rem]">
                      Dunfermline, Fife, Scotland KY11 3PA
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <a
                      href="#"
                      className="text-slate-500 hover:text-cyan-400 hover:drop-shadow-[0_0_10px_rgba(0,255,255,0.8)] transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="text-slate-500 hover:text-cyan-400 hover:drop-shadow-[0_0_10px_rgba(0,255,255,0.8)] transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="text-slate-500 hover:text-cyan-400 hover:drop-shadow-[0_0_10px_rgba(0,255,255,0.8)] transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="text-slate-500 hover:text-cyan-400 hover:drop-shadow-[0_0_10px_rgba(0,255,255,0.8)] transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="text-slate-500 hover:text-cyan-400 hover:drop-shadow-[0_0_10px_rgba(0,255,255,0.8)] transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="text-slate-500 hover:text-cyan-400 hover:drop-shadow-[0_0_10px_rgba(0,255,255,0.8)] transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2-1.68 2.89 2.89 0 0 1 2.89-2.89c.31 0 .6.06.87.16V7.78a6.38 6.38 0 0 0-10.86 4.54A6.38 6.38 0 0 0 6.45 18.7a6.38 6.38 0 0 0 6.38-6.38V8.65a8.31 8.31 0 0 0 6.76 2.51V7.7a4.91 4.91 0 0 1-2-1.01z" />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* Global Support Nodes */}
                <div className="flex flex-col gap-4 font-sans text-sm">
                  <h4 className="text-cyan-600 uppercase text-[10px] tracking-widest font-bold mb-2">
                    Global Support Nodes
                  </h4>
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <span className="text-slate-400 font-medium text-xs">
                      General Inquiries
                    </span>
                    <span className="text-white hover:text-cyan-400 transition-colors cursor-pointer">
                      info@nexoraxdigital.co.uk
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <span className="text-slate-400 font-medium text-xs">
                      24/7 NexoraX Support
                    </span>
                    <span className="text-white hover:text-cyan-400 transition-colors cursor-pointer">
                      support@nexoraxdigital.co.uk
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <span className="text-slate-400 font-medium text-xs">
                      Freephone
                    </span>
                    <span className="text-white hover:text-cyan-400 transition-colors cursor-pointer">
                      {content.globalSupportNumber}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-4 font-sans text-sm justify-end w-full md:w-auto h-full min-w-[280px]">
                  <div className="relative w-full h-40 rounded-xl overflow-hidden border border-[#00f0ff]/30 group shadow-[0_0_30px_rgba(0,240,255,0.15)] flex justify-end">
                    <iframe
                      src="https://maps.google.com/maps?q=Dunfermline,%20Fife,%20Scotland%20KY11%203PA&t=m&z=12&ie=UTF8&iwloc=&output=embed"
                      width="100%"
                      height="100%"
                      style={{
                        border: 0,
                        filter:
                          "invert(100%) hue-rotate(180deg) contrast(120%) brightness(90%)",
                      }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="absolute inset-0 z-10 opacity-70 group-hover:opacity-100 transition-opacity duration-700"
                    ></iframe>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#020202]/90 z-20 pointer-events-none"></div>
                    <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(0,240,255,0.3)] z-30 pointer-events-none rounded-xl"></div>
                    <div className="relative z-40 p-4 text-right flex flex-col justify-end items-end h-full w-full pointer-events-none">
                      <div className="inline-flex items-center gap-2 mb-1 bg-black/60 px-2 py-1 rounded backdrop-blur-md border border-[#00f0ff]/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff] animate-ping shadow-[0_0_10px_#00f0ff]"></span>
                        <p className="font-mono text-[9px] text-[#00f0ff] uppercase tracking-[0.3em] font-black drop-shadow-[0_0_8px_rgba(0,240,255,0.8)]">
                          GLOBAL H.Q.
                        </p>
                      </div>
                      <div className="bg-black/60 px-2 py-1 rounded mt-1 backdrop-blur-md border border-white/5">
                        <p className="text-slate-300 font-mono text-[10px] uppercase font-bold tracking-widest drop-shadow-md">
                          Dunfermline, Fife
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Secured Payment Infrastructure */}
              <div className="max-w-6xl mx-auto border-t border-white/10 pt-8 w-full">
                <NexoraXPaymentTabs />
              </div>
            </footer>

            {/* NexoraX Legal Footer */}
            <NexoraXLegalFooter onNavigate={setActiveLandingTab} activeRoute={activeLandingTab} />

            {showGlobalCookieBanner && activeLandingTab !== null && (
              <div
                id="global-cookie-banner"
                className="fixed bottom-4 left-4 right-4 md:left-auto md:max-w-md md:right-4 z-[9999] bg-black/95 border border-purple-500/30 rounded-2xl p-5 shadow-[0_10px_40px_rgba(168,85,247,0.3)] backdrop-blur-lg flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-5 duration-300 pointer-events-auto"
              >
                <div className="flex gap-3">
                  <div className="p-2 bg-purple-950/40 border border-purple-500/20 rounded-xl text-cyan-400 self-start">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-sans font-bold text-xs text-white uppercase tracking-wider">Privacy Consent Update</h4>
                    <p className="font-sans text-[11px] text-slate-300 leading-relaxed">
                      NexoraX uses cookies to optimize your performance target dashboard and verify secure token handshakes. Choose your preference.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-1 font-mono text-[9px] uppercase tracking-wider font-extrabold">
                  <button
                    id="cookie-banner-accept"
                    onClick={() => {
                      const allOn = {
                        strictlyNecessary: true,
                        preferences: true,
                        analytics: true,
                        marketing: true,
                        chatPersonalisation: true,
                        firstPartyAds: true
                      };
                      localStorage.setItem("nexorax_cookie_consent_v2", JSON.stringify(allOn));
                      setShowGlobalCookieBanner(false);
                      window.dispatchEvent(new CustomEvent("nexorax-consent-updated", { detail: allOn }));
                    }}
                    className="flex-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-cyan-400 text-black rounded-lg hover:opacity-90 active:scale-97 transition cursor-pointer text-center"
                  >
                    Accept All
                  </button>
                  <button
                    id="cookie-banner-reject"
                    onClick={() => {
                      const essentialOnly = {
                        strictlyNecessary: true,
                        preferences: false,
                        analytics: false,
                        marketing: false,
                        chatPersonalisation: false,
                        firstPartyAds: false
                      };
                      localStorage.setItem("nexorax_cookie_consent_v2", JSON.stringify(essentialOnly));
                      setShowGlobalCookieBanner(false);
                      window.dispatchEvent(new CustomEvent("nexorax-consent-updated", { detail: essentialOnly }));
                    }}
                    className="flex-1 px-3 py-2 bg-zinc-950 border border-white/5 text-white hover:bg-zinc-800 active:scale-97 transition cursor-pointer text-center"
                  >
                    Reject Non-Essential
                  </button>
                  <button
                    id="cookie-banner-manage"
                    onClick={() => {
                      setActiveLandingTab("/cookie-settings");
                      setShowGlobalCookieBanner(false);
                    }}
                    className="flex-1 px-3 py-2 bg-purple-950/20 border border-purple-500/20 text-[#00ffd2] hover:bg-purple-950/40 active:scale-97 transition cursor-pointer text-center"
                  >
                    Manage Choices
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          // DIMENSION 2: HIGH-DENSITY VAULT (Existing App layout)
          <motion.div
            key="dimension2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="flex flex-col h-full w-full z-10 overflow-hidden relative select-none"
            style={{
              WebkitUserSelect: "none",
              userSelect: "none",
              WebkitTouchCallout: "none",
            }}
          >
            {/* NexoraX Termination (Kill-Switch) */}
            {isSecViolation && (
              <div className="absolute inset-0 z-[99999] bg-red-900 flex items-center justify-center animate-pulse">
                <div className="bg-black border border-red-500 p-8 md:p-12 rounded-xl text-center shadow-[0_0_100px_rgba(239,68,68,1)] max-w-2xl mx-4">
                  <span className="text-8xl shrink-0 drop-shadow-[0_0_20px_rgba(239,68,68,0.8)]">
                    🚨
                  </span>
                  <h2 className="text-3xl md:text-5xl font-black font-sans text-red-500 uppercase tracking-tighter mt-6 mb-4">
                    SECURITY VIOLATION
                  </h2>
                  <p className="text-xs md:text-sm font-mono text-white uppercase tracking-widest leading-relaxed">
                    UNAUTHORIZED EXTRACTION ATTEMPT.
                    <br />
                    NEXORAX ACCESS PERMANENTLY REVOKED.
                  </p>
                </div>
              </div>
            )}

            {/* Shield Focus Lost Blur */}
            {isBlurred && (
              <div className="absolute inset-0 z-[9999] bg-black/80 backdrop-blur-[20px] flex items-center justify-center">
                <div className="text-white font-mono uppercase tracking-widest text-sm font-bold opacity-50 px-4 text-center">
                  NexoraX Focus Lost. Shield Activated.
                </div>
              </div>
            )}

            {/* Global God-Mode 15-Minute Countdown */}
            {!isStaff && vaultPhase === "vault" && phaseTimeLeft !== null && (
              <div className="absolute top-20 right-6 z-[100] bg-black/80 border border-red-500/50 rounded-xl p-4 backdrop-blur-md shadow-[0_0_30px_rgba(239,68,68,0.2)] w-64 animate-in fade-in slide-in-from-right">
                <div className="flex flex-col items-center justify-center space-y-2">
                  <div className="text-[10px] font-mono text-red-500 uppercase tracking-widest font-bold text-center">
                    God-Mode Access
                  </div>
                  <div className="text-3xl font-mono font-black text-white glow-text-red">
                    {formatTime(phaseTimeLeft)}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400 uppercase tracking-widest text-center mt-2 border-t border-white/10 pt-2 w-full">
                    Leaking Revenue Visualized
                  </div>
                </div>
              </div>
            )}

            {/* Extraction Gate Modal (2-Minute Siphon) */}
            {!isStaff && vaultPhase === "extraction" && (
              <div className="absolute inset-0 z-[200] bg-black/95 backdrop-blur-2xl flex items-center justify-center p-4">
                <div className="bg-[#050505] border-[2px] border-red-500/60 rounded-3xl p-8 md:p-12 max-w-lg w-full shadow-[0_0_80px_rgba(239,68,68,0.3)] relative overflow-hidden text-center">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent pointer-events-none"></div>

                  <div className="text-5xl font-mono font-black text-red-500 animate-pulse mb-6 drop-shadow-[0_0_15px_rgba(239,68,68,0.8)]">
                    {phaseTimeLeft !== null
                      ? formatTime(phaseTimeLeft)
                      : "02:00"}
                  </div>
                  <h2 className="text-2xl font-bold font-sans text-white uppercase tracking-tight relative z-10 mb-4">
                    NexoraX Access Suspended
                  </h2>
                  <p className="text-xs font-mono text-slate-400 mb-8 leading-relaxed relative z-10">
                    Identity verification required to proceed to the Reclamation
                    Deck.
                  </p>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setVaultPhase("plan");
                      setPhaseTimeLeft(8 * 60);
                    }}
                    className="space-y-4 relative z-10 text-left"
                  >
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase font-mono text-red-400 tracking-widest font-bold">
                        Full Name
                      </label>
                      <input
                        required
                        type="text"
                        placeholder="ENTER NAME..."
                        className="w-full bg-black/80 border border-slate-800 rounded h-12 px-4 text-sm font-mono focus:border-red-500/70 outline-none text-white transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase font-mono text-red-400 tracking-widest font-bold">
                        Enterprise Email
                      </label>
                      <input
                        required
                        type="email"
                        placeholder="ENTER EMAIL..."
                        className="w-full bg-black/80 border border-slate-800 rounded h-12 px-4 text-sm font-mono focus:border-red-500/70 outline-none text-white transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase font-mono text-red-400 tracking-widest font-bold">
                        Direct Phone Number
                      </label>
                      <input
                        required
                        type="tel"
                        placeholder="ENTER PHONE..."
                        className="w-full bg-black/80 border border-slate-800 rounded h-12 px-4 text-sm font-mono focus:border-red-500/70 outline-none text-white transition-colors"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-500 transition-colors text-white text-[11px] font-bold uppercase tracking-[0.2em] py-4 rounded shadow-[0_0_15px_rgba(239,68,68,0.5)] mt-6"
                    >
                      VERIFY IDENTITY
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Neural Overwrite (8-Minute Plan Selection) */}
            {!isStaff && vaultPhase === "plan" && (
              <div className="absolute inset-0 z-[200] bg-black/95 backdrop-blur-2xl flex items-center justify-center p-4">
                <div className="max-w-6xl w-full text-center space-y-12 relative z-10">
                  <div className="space-y-6">
                    <div className="text-6xl md:text-8xl font-mono font-black text-cyan-500 animate-pulse drop-shadow-[0_0_30px_rgba(6,182,212,0.8)]">
                      {phaseTimeLeft !== null
                        ? formatTime(phaseTimeLeft)
                        : "08:00"}
                    </div>
                    <h2 className="text-4xl md:text-5xl font-sans font-bold text-white tracking-tight uppercase px-4 glow-text-cyan">
                      Reclamation Deck Unlocked
                    </h2>
                    <p className="text-sm md:text-base font-mono text-slate-400 uppercase tracking-widest max-w-3xl mx-auto px-4">
                      Select your extraction trajectory. Failure to initialize a
                      plan within the designated window will result in permanent
                      node lockout.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 px-4">
                    {[
                      {
                        title: "7-DAY SPRINT",
                        subtitle: "Rapid Ignition",
                        color:
                          "border-cyan-500/30 from-cyan-900/40 text-cyan-400",
                        hoverGlow: "group-hover:text-cyan-300",
                      },
                      {
                        title: "15-DAY EXPANSION",
                        subtitle: "Velocity Scaling",
                        color:
                          "border-indigo-500/30 from-indigo-900/40 text-indigo-400",
                        hoverGlow: "group-hover:text-indigo-300",
                      },
                      {
                        title: "30-DAY NEXORAX MASTERY",
                        subtitle: "Apex Domination",
                        color:
                          "border-purple-500/30 from-purple-900/40 text-purple-400",
                        hoverGlow: "group-hover:text-purple-300",
                      },
                    ].map((plan, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          setIsVaultUnlocked(false);
                          setVaultPhase(null);
                          setPhaseTimeLeft(null);
                          setActiveLandingTab("Tiers");
                        }}
                        className={`glass-panel border rounded-3xl p-10 flex flex-col items-center justify-center bg-gradient-to-b to-transparent hover:-translate-y-2 transition-all duration-300 cursor-pointer group shadow-[0_0_40px_rgba(0,0,0,0.3)] ${plan.color}`}
                      >
                        <h3
                          className={`text-2xl lg:text-3xl font-bold font-sans tracking-tight mb-3 text-white ${plan.hoverGlow} transition-colors text-center`}
                        >
                          {plan.title}
                        </h3>
                        <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] opacity-80 text-center mb-10 text-slate-300">
                          {plan.subtitle}
                        </p>
                        <button className="text-[10px] uppercase font-mono px-8 py-3.5 border border-white/20 rounded font-bold bg-black/80 text-white group-hover:border-current transition-colors w-full tracking-widest">
                          INITIALIZE PLAN
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Permanent Lockout */}
            {!isStaff && vaultPhase === "locked" && (
              <div className="absolute inset-0 z-[300] bg-black flex items-center justify-center p-4">
                <div className="text-center space-y-6">
                  <span className="text-8xl shrink-0 drop-shadow-[0_0_20px_rgba(239,68,68,0.8)]">
                    🛑
                  </span>
                  <h2 className="text-4xl font-black font-sans text-red-500 uppercase tracking-tighter mix-blend-screen">
                    Node Permanently Locked
                  </h2>
                  <p className="text-xs font-mono text-slate-500 uppercase tracking-widest max-w-md mx-auto">
                    Critical extraction phase failed. NexoraX access has been
                    permanently revoked for this session.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation / Architect Bar */}
            <nav className="h-16 border-b border-white/5 bg-black/60 backdrop-blur-xl flex items-center justify-between px-6 z-10 shrink-0">
              <div className="flex items-center space-x-4">
                <img
                  src="/logo.png?v=2"
                  alt="NexoraX Digital LTD"
                  className="h-16 w-auto drop-shadow-[0_0_15px_rgba(0,195,255,0.8)]"
                />
                <div className="flex flex-col">
                  <span className="font-bold tracking-widest text-lg leading-tight w-full nexorax-text-style">
                    NEXORAX DIGITAL LTD
                  </span>
                  <span className="text-[10px] text-cyan-500/60 uppercase tracking-[0.2em] font-mono leading-none">
                    NexoraX Operating System v4.1
                  </span>
                </div>
              </div>

              {/* Imperial Strike Command Array */}
              <nav className="fixed top-0 left-0 w-full z-[9999] bg-[#020408] border-b border-cyan-500/50 backdrop-blur-xl shadow-[0_0_30px_rgba(0,255,255,0.15)] flex flex-col items-center py-3">
                {/* TOP STRIKE ARRAY */}
                <div className="flex gap-4 w-full justify-center items-center mb-2">
                  <button
                    onClick={() => {
                      setActiveLandingTab(null);
                      setActiveMainTab("REALITY AUDIT LEDGER");
                      setActiveSubTab(null);
                    }}
                    className="bg-[#050914] border border-emerald-500 text-emerald-400 px-6 py-2 rounded font-mono font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-emerald-900/30 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all"
                  >
                    [ REALITY AUDIT NODE ]
                  </button>
                  <button
                    onClick={() => {
                      setActiveLandingTab(null);
                      setActiveMainTab("NEXORAX 27 HUB");
                      setActiveSubTab(null);
                    }}
                    className="bg-[#050914] border border-cyan-400 text-cyan-300 px-6 py-2 rounded font-mono font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-cyan-950/30 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all"
                  >
                    [ NEXORAX 27 HUB ]
                  </button>
                  <button
                    onClick={() => {
                      setActiveLandingTab(null);
                      setActiveMainTab("NEXORAX 27 WORKSPACE");
                      setActiveSubTab(null);
                    }}
                    className="bg-[#050914] border border-[#e840f3]/75 text-fuchsia-300 px-6 py-2 rounded font-mono font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-fuchsia-950/20 hover:shadow-[0_0_20px_rgba(232,64,243,0.3)] transition-all"
                  >
                    [ 27 WORKSPACE TABS ]
                  </button>
                  <button
                    onClick={() => {
                      setActiveLandingTab(null);
                      setActiveMainTab("NEXORAX ECOSYSTEM 27 HUB");
                      setActiveSubTab(null);
                    }}
                    className="bg-[#050914] border border-[#e840f3] text-[#e840f3] px-6 py-2 rounded font-mono font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-fuchsia-950/30 hover:shadow-[0_0_20px_rgba(232,64,243,0.4)] transition-all"
                  >
                    [ ECOSYSTEM METRICS ]
                  </button>
                  <button
                    onClick={() => setShowFiscalStrike(true)}
                    className="bg-[#050914] border border-cyan-500 text-cyan-400 px-6 py-2 rounded font-mono font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-cyan-900/30 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all"
                  >
                    [ ACCOUNTANT NODE ]
                  </button>
                  <button
                    onClick={() => setShowLegalStrike(true)}
                    className="bg-[#050914] border border-amber-500 text-amber-500 px-6 py-2 rounded font-mono font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-amber-900/30 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all"
                  >
                    [ LAWYER NODE ]
                  </button>
                  <button
                    onClick={() => setShowArchitectVoice(true)}
                    className="bg-[#050914] border border-indigo-500 text-indigo-400 px-6 py-2 rounded font-mono font-bold text-[11px] uppercase tracking-[0.2em] hover:bg-indigo-900/30 hover:shadow-[0_0_20px_rgba(99,102,241,0.4)] transition-all"
                  >
                    [ TALK TO ARCHITECT ]
                  </button>
                </div>

                {/* SUB-NAV CONTEXT */}
                <div className="w-full h-px bg-white/5 my-1" />
                <div className="flex items-center justify-between w-full px-8 text-[10px] font-mono text-slate-500 uppercase tracking-[0.2em]">
                  <span>
                    NEXORAX CORE // {currentTime.toLocaleTimeString()}
                  </span>
                  <span>
                    STATUS: <span className="text-emerald-500">OPTIMIZED</span>
                  </span>
                </div>
              </nav>

              <div className="hidden lg:flex items-center space-x-6">
                <div className="flex space-x-4 text-[11px] font-mono uppercase tracking-wider">
                  <span className="text-cyan-400 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />{" "}
                    <span className="opacity-40">LAT:</span> 51.5074 N
                  </span>
                  <span className="text-indigo-400">
                    <span className="opacity-40">SOV:</span> £150,000 VAULT
                  </span>
                  <span
                    className={
                      content.statusIndicator === "maintenance"
                        ? "text-amber-400"
                        : content.statusIndicator === "lockdown"
                          ? "text-rose-400"
                          : "text-emerald-400"
                    }
                  >
                    <span className="opacity-40">STAT:</span>{" "}
                    {content.statusIndicator === "maintenance"
                      ? "DOWNTIME"
                      : content.statusIndicator === "lockdown"
                        ? "LOCKDOWN"
                        : "OPTIMIZED"}
                  </span>
                </div>
                <div className="h-8 w-px bg-white/10"></div>
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-cyan-900 to-indigo-900 border border-white/20 flex items-center justify-center cursor-pointer">
                  <span className="text-[10px] text-white">CH</span>
                </div>
              </div>

              {/* Mobile Hamburger menu toggle button for Workspace */}
              <div className="lg:hidden flex items-center">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 border border-cyan-500/35 hover:border-cyan-400 bg-cyan-950/15 rounded-lg text-cyan-400 hover:text-white transition-all cursor-pointer flex items-center justify-center"
                  aria-label="Toggle Workspace Mobile Menu"
                >
                  <Menu className="w-5 h-5 text-cyan-400" />
                </button>
              </div>
            </nav>

            {/* Main Container */}
            <main className="flex-1 flex flex-col lg:flex-row overflow-hidden z-10">
              {/* Left Sidebar */}
              <aside className="w-full h-[35%] lg:h-full lg:w-[30%] lg:max-w-sm lg:min-w-[320px] border-b lg:border-b-0 lg:border-r border-white/5 bg-black/40 backdrop-blur-sm p-4 flex flex-col custom-scrollbar overflow-y-auto shrink-0">
                <div className="text-xs font-bold text-slate-300 uppercase tracking-[0.12em] mb-4 flex items-center shrink-0">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full mr-2"></div>
                  NexoraX Matrix Categories
                </div>
                <div className="flex-1 min-h-0 flex flex-col">
                  <HolographicDatabase 
                    onSelectCategory={(categoryTitle) => {
                      setActiveLandingTab(null);
                      setActiveMainTab(categoryTitle);
                      setActiveSubTab(null);
                      const category = categoryOrder.find(
                        (c) => c.title === categoryTitle,
                      );
                      if (category) {
                        setActiveDropdown(category.id);
                      }
                    }}
                    onShowArchitectVoice={(mode?: 'standard' | 'concierge') => {
                      setVoiceAssistantMode(mode || 'standard');
                      setShowArchitectVoice(true);
                    }}
                  />
                </div>
              </aside>

              {/* Center Panel */}
              <div className="flex-1 flex flex-col bg-black/20 p-4 md:p-6 space-y-4 md:space-y-6 overflow-hidden min-w-0">
                <div className="flex-1 min-h-0 flex flex-col relative">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeMainTab}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0 flex flex-col"
                    >
                      {activeMainTab === "NODE 0: ARCHITECT TERMINAL" ? (
                        isDirector ? <Node0Terminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "DYNAMIC PAYLOAD MATRIX" ? (
                        isDirector ? <PayloadMatrix /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "WEBSITE SITE EDITOR" ? (
                        isDirector ? <SiteEditor /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "BRANDING COMMAND" ? (
                        isDirector ? <BrandingTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "CRM, DASHBOARD & SLIDE DECK" ? (
                        isDirector ? <CrmDashboardTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "SECRETARY NODE" ? (
                        isDirector ? (
                          <SecretaryNode onNavigate={(tab, subTab) => {
                            setActiveLandingTab(null);
                            setActiveMainTab(tab);
                            setActiveSubTab(subTab || null);
                            setActiveDropdown(null);
                          }} />
                        ) : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "REALITY AUDIT LEDGER" ? (
                        isDirector ? <RealityAuditTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "ROGUE ENTITY ELIMINATION" ? (
                        isDirector ? <RogueEntityTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "SPECTACLE COMMAND" ? (
                        isDirector ? <SpectacleCommandTerminal /> : <div className="p-10 font-mono text-xs text-rose-500 uppercase tracking-widest bg-black/60 border border-red-500/20 rounded-2xl">Access Denied: Only Authorized Directors can view legacy simulation nodes.</div>
                      ) : activeMainTab === "Admin Panel" || activeMainTab === "ADMIN_PANEL" ? (
                        <AdminControlSafeFallback />
                      ) : activeMainTab === "NEXORAX 27 HUB" ? (
                        <div className="w-full h-full overflow-y-auto custom-scrollbar p-2">
                          <NexoraX27Hub 
                            onNavigateToCategory={(title, id) => {
                              setSelectedCategoryId(id);
                              setActiveLandingTab(null);
                              if (id === 27) {
                                setActiveMainTab("NEXORAX CATEGORY 27 CONSOLE");
                              } else {
                                setActiveMainTab("NEXORAX 27 WORKSPACE");
                              }
                              setActiveSubTab(null);
                            }}
                            onNavigateToDiagnostic={(id) => {
                              setSelectedCategoryId(id);
                              setActiveLandingTab(null);
                              setActiveMainTab("NEXORAX ECOSYSTEM 27 HUB");
                              setActiveSubTab(null);
                            }}
                          />
                        </div>
                      ) : activeMainTab === "NEXORAX 27 WORKSPACE" ? (
                        <div className="w-full h-full overflow-y-auto custom-scrollbar p-2">
                          <NexoraX27WorkspacePage 
                            initialCategoryId={selectedCategoryId || 1}
                            onSelectCategory={(id) => {
                              setSelectedCategoryId(id);
                            }}
                          />
                        </div>
                      ) : activeMainTab === "NEXORAX CATEGORY 27 CONSOLE" ? (
                        <div className="w-full h-full overflow-y-auto custom-scrollbar p-2">
                          <NexoraXCategory27Console 
                            onBackToHub={() => {
                              setActiveMainTab("NEXORAX 27 HUB");
                            }}
                          />
                        </div>
                      ) : activeMainTab === "NEXORAX ECOSYSTEM 27 HUB" ? (
                        <div className="w-full h-full overflow-y-auto custom-scrollbar p-2">
                          <NexoraX27CategoriesHub 
                            selectedCategoryId={selectedCategoryId}
                            onSelectCategory={(id) => {
                              setSelectedCategoryId(id);
                            }}
                          />
                        </div>
                      ) : activeMainTab === "Company Divisions" ? (
                        <CosmicLandingTabPanel
                          title="Company Divisions"
                          onSelectCategory={(categoryTitle) => {
                            setActiveLandingTab(null);
                            setActiveMainTab(categoryTitle);
                            setActiveSubTab(null);
                            const category = categoryOrder.find(
                              (c) => c.title === categoryTitle,
                            );
                            if (category) {
                              setActiveDropdown(category.id);
                            }
                          }}
                        />
                      ) : (
                        <CosmicTabPanel
                          title={activeMainTab}
                          defaultSelectedNode={activeSubTab}
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* STATS BENTO SECTION */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 h-auto md:h-28 shrink-0 pb-1 md:pb-0">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex flex-col justify-center">
                    <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                      Capital Vault
                    </span>
                    <span className="text-2xl font-bold text-white tracking-tighter italic">
                      £5,102,443
                    </span>
                    <div className="h-1 bg-cyan-500/20 mt-2 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-400 w-[72%]"></div>
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex flex-col justify-center">
                    <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                      Active Swarms
                    </span>
                    <span className="text-2xl font-bold text-white tracking-tighter italic">
                      1,244
                    </span>
                    <span className="text-[10px] text-emerald-400 font-mono">
                      +12.4% PERFORMANCE
                    </span>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex flex-col justify-center">
                    <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                      Threat Level
                    </span>
                    <span className="text-2xl font-bold text-white tracking-tighter italic">
                      ZERO
                    </span>
                    <span className="text-[10px] text-indigo-400 font-mono">
                      QUANTUM API SECURE
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Sidebar */}
              <aside className="w-full h-[35%] lg:h-full lg:w-[30%] lg:max-w-[400px] lg:min-w-[320px] border-t lg:border-t-0 lg:border-l border-white/5 bg-black/40 backdrop-blur-sm p-4 flex flex-col custom-scrollbar overflow-y-auto shrink-0">
                <div className="text-xs font-bold text-slate-300 uppercase tracking-[0.12em] mb-4 flex items-center shrink-0">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                  Protocol Pipeline
                </div>
                <div className="shrink-0 mb-6 font-sans">
                  <ExtractionFunnel />
                </div>
                <div className="text-xs font-bold text-slate-300 uppercase tracking-[0.12em] mb-4 flex items-center shrink-0">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                  System Status
                </div>
                <div className="flex-1 space-y-2 font-mono text-[11px] leading-relaxed text-slate-300">
                  <div className="p-2 border border-white/5 rounded bg-black/30">
                    <span className="text-emerald-400 font-bold">CONNECTION:</span>{" "}
                    SECURE_UPLINK
                  </div>
                  <div className="p-2 border border-white/5 rounded bg-black/30">
                    <span className="text-cyan-400 font-bold">DEFENSE:</span>{" "}
                    ACTIVE_GUARDIAN_02
                  </div>
                </div>
              </aside>
            </main>

            {/* Footer Status Bar */}
            <footer className="h-8 border-t border-white/5 bg-black/80 px-4 flex items-center justify-between z-10 shrink-0">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2 text-[9px] font-mono text-emerald-500 uppercase">
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping"></span>
                  <span>Uptime: 99.999%</span>
                </div>
                <div className="text-[9px] font-mono text-slate-500 uppercase">
                  Node-ID: 0x9f22...c10b
                </div>
                <div className="text-[9px] font-mono text-slate-500 uppercase hidden md:block">
                  Nuremberg Protocols Active
                </div>
              </div>
              <div className="flex items-center space-x-3 sm:space-x-4 text-[9px] font-mono text-slate-500 uppercase">
                <span className="hidden sm:inline">
                  Global Grid Capacity: 84%
                </span>
                <div className="w-16 sm:w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 w-[84%]"></div>
                </div>
                <span className="text-white">
                  UTC {currentTime.toISOString().split("T")[1].split(".")[0]}
                </span>
              </div>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>

      {showLegalStrike && (
        <LegalStrikeModal onClose={() => setShowLegalStrike(false)} />
      )}
      {showFiscalStrike && (
        <FiscalStrikeModal
          onClose={() => setShowFiscalStrike(false)}
          onAlert={() => setHasCriticalAlert(true)}
         />
      )}
      {showHotkeyHelp && (
        <NexoraXHotkeyHelpModal onClose={() => setShowHotkeyHelp(false)} />
      )}
      
      <NexoraXConfirmationDialog />

      {/* MOBILE COMPACT DRAWER FOR WORKSPACE TIER */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[10000] bg-[#02040b]/98 flex flex-col p-6 lg:hidden font-mono text-[11px] text-slate-300 pointer-events-auto"
          >
            {/* Header */}
            <div className="flex justify-between items-center border-b border-cyan-500/20 pb-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse" />
                <span className="font-sans font-black tracking-widest text-[#00f0ff] uppercase text-xs">
                  NEXORAX MOBILE SYSTEM COMMAND
                </span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 border border-slate-800 hover:border-cyan-400 rounded text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quick-links for mobile navigate */}
            <div className="flex-1 space-y-3 overflow-y-auto pr-1">
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-2 px-1">
                SECURE RUNTIME NAVIGATION:
              </span>
              <button
                onClick={() => {
                  setActiveLandingTab(null);
                  setActiveMainTab("REALITY AUDIT LEDGER");
                  setActiveSubTab(null);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left py-3.5 px-4 bg-[#050914] border border-emerald-500/30 rounded-xl text-emerald-400 font-bold hover:bg-emerald-950/25 transition-all text-xs"
              >
                [ REALITY AUDIT NODE ]
              </button>
              <button
                onClick={() => {
                  setActiveLandingTab(null);
                  setActiveMainTab("NEXORAX 27 HUB");
                  setActiveSubTab(null);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left py-3.5 px-4 bg-[#050914] border border-cyan-400/30 rounded-xl text-cyan-300 font-bold hover:bg-cyan-950/25 transition-all text-xs"
              >
                [ NEXORAX 27 HUB ]
              </button>
              <button
                onClick={() => {
                  setActiveLandingTab(null);
                  setActiveMainTab("NEXORAX 27 WORKSPACE");
                  setActiveSubTab(null);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left py-3.5 px-4 bg-[#050914] border border-fuchsia-500/30 rounded-xl text-fuchsia-300 font-bold hover:bg-fuchsia-950/25 transition-all text-xs"
              >
                [ 27 WORKSPACE TABS ]
              </button>
              <button
                onClick={() => {
                  setActiveLandingTab(null);
                  setActiveMainTab("NEXORAX ECOSYSTEM 27 HUB");
                  setActiveSubTab(null);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left py-3.5 px-4 bg-[#050914] border border-purple-500/30 rounded-xl text-purple-400 font-bold hover:bg-purple-950/25 transition-all text-xs"
              >
                [ ECOSYSTEM METRICS ]
              </button>
              <button
                onClick={() => {
                  setShowFiscalStrike(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left py-3.5 px-4 bg-[#050914] border border-cyan-500/30 rounded-xl text-cyan-400 font-bold hover:bg-cyan-900/25 transition-all text-xs"
              >
                [ ACCOUNTANT NODE ]
              </button>
              <button
                onClick={() => {
                  setShowLegalStrike(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left py-3.5 px-4 bg-[#050914] border border-amber-500/30 rounded-xl text-amber-400 font-bold hover:bg-amber-900/25 transition-all text-xs"
              >
                [ LAWYER NODE ]
              </button>
            </div>

            {/* Logout CTA at the bottom */}
            <div className="pt-6 border-t border-slate-900 mt-auto space-y-3">
              <button
                onClick={async () => {
                  setIsMobileMenuOpen(false);
                  await handleHeaderLogout();
                }}
                className="w-full py-3.5 px-4 bg-rose-950/45 hover:bg-rose-900/60 border border-rose-500/45 text-rose-300 hover:text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-xs cursor-pointer shadow-[0_0_15px_rgba(244,63,94,0.15)]"
              >
                <LogOut className="w-4 h-4 text-rose-400 shrink-0" />
                <span>TERMINATE WORKSPACE (SIGN OUT)</span>
              </button>
              <div className="text-center text-[8.5px] text-slate-600 uppercase tracking-widest pt-1.5 font-mono">
                SECURE END-SESSION ENVELOPE VERIFIED
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FLOATING ADMIN HOTKEY TOAST TRIGGER INDICATOR */}
      <AnimatePresence>
        {lastTriggeredHotkey && (
          <motion.div
            key="hotkey-toast"
            initial={{ opacity: 0, y: 50, x: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, x: 10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 350, damping: 25 }}
            className="fixed bottom-12 right-6 z-[9999] bg-[#02050f]/95 border border-violet-500/50 rounded-xl p-4 shadow-[0_0_35px_rgba(139,92,246,0.5)] max-w-sm overflow-hidden text-left"
          >
            {/* Ambient thin glowing status strip */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-violet-500 via-fuchsia-500 to-[#00ffff]" />
            <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-violet-600/10 blur-[30px] rounded-full pointer-events-none" />

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded bg-violet-950/40 border border-violet-500/40 flex items-center justify-center shrink-0">
                <Keyboard className="w-4 h-4 text-violet-400 animate-pulse" />
              </div>
              <div className="flex-1 min-w-0 pr-2">
                <span className="text-[8px] font-mono text-violet-400 uppercase tracking-[0.25em] block">
                  SYSTEM CORE HOTKEY TRIGGERED
                </span>
                <h4 className="text-xs font-bold text-white uppercase tracking-tight mt-0.5 truncate">
                  {lastTriggeredHotkey.name}
                </h4>
                <p className="text-[9px] text-zinc-400 font-mono mt-1 flex items-center gap-1.5 uppercase tracking-wide">
                  <span className="text-zinc-500">ACTION:</span> 
                  <span className="text-fuchsia-300 font-bold bg-fuchsia-950/20 px-1.5 py-0.5 rounded border border-fuchsia-500/10 text-[8px]">{lastTriggeredHotkey.action}</span>
                </p>
                <div className="mt-2 flex items-center justify-between text-[8px] font-mono text-zinc-500">
                  <span>KEY: <strong className="text-cyan-400 font-bold">{lastTriggeredHotkey.keyString}</strong></span>
                  <span className="text-zinc-650 font-bold text-[#00ffff]">HANDSHAKE 0.8s</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FLOATING INACTIVITY SESSION WARNING TOAST (60 SECONDS BEFORE SESSION DISCONNECTIONS) */}
      <AnimatePresence>
        {showInactivityWarning && (
          <motion.div
            key="inactivity-warning-toast"
            initial={{ opacity: 0, y: 50, x: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, x: -10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 350, damping: 25 }}
            className="fixed bottom-12 left-6 z-[9999] bg-[#02050f]/95 border border-amber-500/50 rounded-xl p-4 shadow-[0_0_35px_rgba(245,158,11,0.4)] max-w-sm overflow-hidden text-left font-mono"
          >
            {/* Ambient thin glowing status strip */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-amber-600/10 blur-[30px] rounded-full pointer-events-none" />

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded bg-amber-950/40 border border-amber-500/40 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4 text-amber-400 animate-bounce" />
              </div>
              <div className="flex-1 min-w-0 pr-2">
                <span className="text-[8px] font-mono text-amber-400 uppercase tracking-[0.25em] block">
                  SECURITY CONTROL MATRIX WARNING
                </span>
                <h4 className="text-xs font-bold text-white uppercase tracking-tight mt-0.5 redact">
                  INACTIVITY LIMIT TRIGGERED
                </h4>
                <p className="text-[9.5px] text-zinc-350 mt-1 leading-relaxed uppercase">
                  Your session will expire in <strong className="text-rose-400 text-sm font-black underline animate-pulse">{900 - inactivitySeconds}s</strong>. Press any key or move your mouse to extend clearance.
                </p>
                <div className="mt-2 flex items-center justify-between text-[8px] text-zinc-500 uppercase">
                  <span>MAX SESSION: 15 MINUTES</span>
                  <span className="text-amber-500 font-bold animate-pulse">AUTO-LOCK SEQUENCE</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FULLSCREEN LOCKOUT COVER FOR EXPIRED SESSIONS */}
      {inactivitySeconds >= 900 && (
        <div className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center p-4 text-center font-mono select-none">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-rose-500" />
          <p className="text-xs text-emerald-400 font-extrabold tracking-wide mb-4 text-center">
            "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
          </p>
          <div className="p-6 bg-[#02050e] border border-cyan-500/30 rounded-3xl shadow-[0_0_50px_rgba(6,182,212,0.15)] max-w-md w-full relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-cyan-500/5 blur-[50px] rounded-full pointer-events-none" />
            <div className="w-16 h-16 mx-auto mb-4 bg-cyan-950/40 border border-cyan-500/40 rounded-full flex items-center justify-center text-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
              <Lock className="w-8 h-8 animate-pulse" />
            </div>
            
            <span className="text-[8.5px] text-cyan-400 tracking-[0.3em] uppercase block font-black">NEXORAX SECURITY CORE</span>
            <h3 className="text-xl font-sans font-black text-white uppercase tracking-tight mt-1 mb-2">SESSION AUTO-LOCKED</h3>
            <p className="text-[10px] text-slate-400 leading-relaxed uppercase mb-6">
              To protect enterprise assets under SC887095, your terminal has been auto-shielded due to 15 minutes of uninterrupted inactivity.
            </p>
            
            <button
              onClick={() => {
                setInactivitySeconds(0);
                setShowInactivityWarning(false);
              }}
              style={{ cursor: 'pointer' }}
              className="w-full py-3 bg-cyan-950/60 border border-cyan-500/40 hover:bg-cyan-500 text-cyan-300 hover:text-black font-extrabold uppercase text-xs tracking-widest rounded-xl transition-all cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.1)] hover:shadow-[0_0_25px_rgba(6,182,212,0.3)]"
            >
              RE-AUTHORIZE SECURE ENVELOPE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
